/**
 * API Service Layer for UsedFindr
 * Handles all communication with Django REST Framework API
 * Provides field name conversion between frontend (camelCase) and API (snake_case)
 */

import { toSnakeCase, toCamelCase } from '../utilities/base/index.js';

// Initialize logger for this module
const logger = window.Logger ? window.Logger.create('api-service') : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: console.error.bind(console)
};

/** Hashed chunk name for stale-bundle detection (e.g. api-service.ABC123.js). */
function getApiServiceBuildMarker() {
  try {
    const url = import.meta?.url || '';
    const match = url.match(/api-service[.][A-Za-z0-9_-]+\.js/);
    if (match) return match[0];
    const tail = (url.split('/').pop() || '').split('?')[0];
    return tail.includes('api-service') ? tail : 'dev-unbundled';
  } catch {
    return 'unknown';
  }
}

export const API_SERVICE_BUILD_MARKER = getApiServiceBuildMarker();

/**
 * Dimensions the cookieless summary can be narrowed by, mirroring FILTERS in
 * CookielessAnalyticsSummaryView. Whitelisted rather than passed straight
 * through so a stray key in a caller's object cannot become a query parameter.
 */
export const COOKIELESS_FILTER_KEYS = [
  'path', 'country', 'device_type', 'browser', 'os', 'referrer',
  'utm_source', 'utm_medium', 'utm_campaign',
  'marketplace', 'destination_domain', 'product_id',
];

if (typeof window !== 'undefined') {
  window.__usedfindrApiServiceBuild = API_SERVICE_BUILD_MARKER;
}

class ApiService {
  constructor() {
    // Detect environment and set API base URL.
    //
    // The cross-origin hop to Django only exists for the Vite dev server, which
    // serves the pages itself on its own port and has no /api. When Django is
    // serving the page — any port — the API is same-origin, exactly as it is in
    // production. Hardcoding :8000 for every localhost origin broke that case:
    // running Django on another port (because something else holds 8000) left
    // every request pointed at a server that was not there, and CSP's
    // connect-src 'self' blocked it before it could even fail.
    const isLocal = window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname === '' ||
                    window.location.protocol === 'file:';
    const VITE_DEV_PORTS = new Set(['5500', '5173', '4173']);
    const servedByViteDev = VITE_DEV_PORTS.has(window.location.port);

    this.baseUrl = isLocal && (servedByViteDev || window.location.protocol === 'file:')
      ? 'http://localhost:8000/api'
      : `${window.location.origin}/api`;
    this.refreshAttempts = 0;
    this.maxRefreshAttempts = 3;
    this.tokenStorageTime = null; // Track when token was last stored
    this.pendingRequests = new Map(); // URL+method -> Promise (for request deduplication)
    this._cachedCsrfToken = null; // CSRF token from API when cross-origin (Phase 1)
    // Phase 3: access token in memory only (not localStorage)
    this._accessToken = null;
    this._refreshPromise = null; // Single in-flight refresh lock
    this.wasAuthenticatedBefore = false; // Track if user ever had a valid token
    // Admin vs consumer: deterministic from current page (no try-one-then-other).
    // Any URL whose pathname contains the segment /admin is admin (e.g. admin.html, admin-login.html, admin-dashboard.html, /admin/...).
    this._isAdminContext = typeof window !== 'undefined' && window.location.pathname.includes('/admin');
    this._adminAuthChannel = null;
    this._initAdminAuthChannel();
  }

  /**
   * Cross-tab in-memory access token sharing (optional fast path).
   * Primary continuity is still the httpOnly refresh cookie + refreshToken().
   */
  _initAdminAuthChannel() {
    if (!this._isAdminContext || typeof BroadcastChannel === 'undefined') return;
    try {
      this._adminAuthChannel = new BroadcastChannel('usedfindr-admin-auth');
      this._adminAuthChannel.addEventListener('message', (event) => {
        const data = event && event.data ? event.data : {};
        if (data.type === 'token' && data.access) {
          if (!this._accessToken) {
            this.setAccessToken(data.access, { broadcast: false });
          }
        } else if (data.type === 'request-token' && this._accessToken) {
          try {
            this._adminAuthChannel.postMessage({ type: 'token', access: this._accessToken });
          } catch (_) { /* ignore */ }
        } else if (data.type === 'clear') {
          this._accessToken = null;
          this.tokenStorageTime = null;
        }
      });
    } catch (e) {
      logger.debug('Admin auth BroadcastChannel unavailable', { error: e && e.message });
      this._adminAuthChannel = null;
    }
  }

  /**
   * Ask other open admin tabs for their in-memory access token (short timeout).
   * @param {number} timeoutMs
   * @returns {Promise<string|null>}
   */
  _requestTokenFromPeerTabs(timeoutMs = 250) {
    if (!this._adminAuthChannel) return Promise.resolve(null);
    return new Promise((resolve) => {
      let settled = false;
      const onMsg = (event) => {
        const data = event && event.data ? event.data : {};
        if (data.type === 'token' && data.access && !settled) {
          settled = true;
          cleanup();
          resolve(data.access);
        }
      };
      const cleanup = () => {
        clearTimeout(timer);
        try {
          this._adminAuthChannel.removeEventListener('message', onMsg);
        } catch (_) { /* ignore */ }
      };
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          cleanup();
          resolve(null);
        }
      }, timeoutMs);
      try {
        this._adminAuthChannel.addEventListener('message', onMsg);
        this._adminAuthChannel.postMessage({ type: 'request-token' });
      } catch (_) {
        settled = true;
        cleanup();
        resolve(null);
      }
    });
  }

  /**
   * Refresh URL for the current context. Admin pages use admin refresh; main site uses consumer.
   * @returns {string}
   */
  getRefreshUrl() {
    return this._isAdminContext
      ? `${this.baseUrl}/auth/admin/token/refresh/`
      : `${this.baseUrl}/auth/token/refresh/`;
  }

  /**
   * Whether the current page is admin context (pathname contains /admin).
   * @returns {boolean}
   */
  isAdminContext() {
    return this._isAdminContext;
  }

  /**
   * Get access token from memory (Phase 3). Consumers use this instead of localStorage.
   * On admin pages, restores a one-time handoff from sessionStorage after redirect from admin-login (full page load would otherwise lose in-memory token).
   * @returns {string|null}
   */
  getAccessToken() {
    if (!this._accessToken && this._isAdminContext && typeof sessionStorage !== 'undefined') {
      const handoff = sessionStorage.getItem('usedfindr_admin_token_handoff');
      if (handoff) {
        sessionStorage.removeItem('usedfindr_admin_token_handoff');
        this.setAccessToken(handoff);
        return handoff;
      }
    }
    return this._accessToken;
  }

  /**
   * Set access token in memory. Used after login/refresh. (Phase 3)
   * @param {string|null} token
   * @param {{ broadcast?: boolean }} [options] - broadcast:false skips BroadcastChannel notify
   */
  setAccessToken(token, options = {}) {
    this._accessToken = token || null;
    this.tokenStorageTime = token ? Date.now() : null;
    if (token) {
      this.wasAuthenticatedBefore = true; // User has authenticated
      this.refreshAttempts = 0; // Successful auth resets refresh budget
      // Keep client Remember-me flag in sync with JWT claim (admin long sessions)
      if (this._isAdminContext && this.accessTokenHasRememberMe(token)) {
        try {
          localStorage.setItem('rememberMe', 'true');
          if (!localStorage.getItem('rememberMeSetAt')) {
            localStorage.setItem('rememberMeSetAt', Date.now().toString());
          }
        } catch (_) { /* ignore */ }
      }
      if (options.broadcast !== false && this._adminAuthChannel) {
        try {
          this._adminAuthChannel.postMessage({ type: 'token', access: token });
        } catch (_) { /* ignore */ }
      }
    }
  }

  /**
   * Decode JWT payload without verifying signature (client-side hints only).
   * @param {string} token
   * @returns {object|null}
   */
  decodeAccessTokenPayload(token) {
    if (!token || typeof token !== 'string' || !token.includes('.')) return null;
    try {
      const payload = token.split('.')[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
      return JSON.parse(atob(padded));
    } catch (_) {
      return null;
    }
  }

  /**
   * True when admin access JWT was issued with remember_me (30-day session).
   * Also treats legacy long-lived access tokens (exp-iat >= 24h) as remember-me —
   * those were issued before the claim was copied onto the access token.
   * @param {string} [token]
   * @returns {boolean}
   */
  accessTokenHasRememberMe(token) {
    const t = token || this.getAccessToken();
    const payload = this.decodeAccessTokenPayload(t);
    if (!payload) return false;
    if (payload.remember_me) return true;
    const iat = Number(payload.iat);
    const exp = Number(payload.exp);
    if (Number.isFinite(iat) && Number.isFinite(exp) && (exp - iat) >= 24 * 3600) {
      return true;
    }
    return false;
  }

  /**
   * Clear in-memory access token and related state. Call on logout and 401. (Phase 3)
   * @param {{ resetRefreshAttempts?: boolean, broadcast?: boolean }} [options]
   */
  clearAccessToken(options = {}) {
    logger.debug('clearAccessToken called');
    this._accessToken = null;
    this.tokenStorageTime = null;
    // When refresh returned 401 we keep refreshAttempts elevated so we don't spin;
    // explicit logout / successful re-auth resets via setAccessToken or resetRefreshAttempts:true.
    if (options.resetRefreshAttempts !== false) {
      this.refreshAttempts = 0;
    }
    this._cachedCsrfToken = null;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('usedfindr_admin_token_handoff');
    }
    localStorage.removeItem('usedfindr_token');
    localStorage.removeItem('usedfindr_refresh_token');
    // Don't remove usedfindr_user here - it's a display cache; only explicit logout clears it
    // Note: Don't reset wasAuthenticatedBefore - we want to track if user was ever logged in
    if (options.broadcast !== false && this._adminAuthChannel) {
      try {
        this._adminAuthChannel.postMessage({ type: 'clear' });
      } catch (_) { /* ignore */ }
    }
  }

  /**
   * Check if an endpoint is public (doesn't require authentication)
   * @param {string} url - The API endpoint URL
   * @returns {boolean} - True if endpoint is public
   */
  isPublicEndpoint(url) {
    // Admin API routes always require auth (e.g. /api/admin/products/..., /api/admin/submissions/...)
    if (url.includes('/admin/')) return false;
    const publicPatterns = [
      '/public/',
      '/products/',  // Products endpoint is public (used on homepage, consumer)
      '/auth/csrf/',
      '/csp-report/',
      '/analytics/anonymous-search',  // POST only; public minimal search stat ingest
    ];
    return publicPatterns.some(pattern => url.includes(pattern));
  }

  /**
   * Get CSRF token from document.cookie (same-origin) or from cached API response (cross-origin).
   * For cookie-based auth, login/refresh/logout need X-CSRFToken header.
   */
  getCsrfToken() {
    if (this._cachedCsrfToken) return this._cachedCsrfToken;
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? decodeURIComponent(match[1].trim()) : '';
  }

  /**
   * Fetch CSRF token from API (sets cookie and returns token). Call before first login/refresh when cross-origin.
   */
  async ensureCsrfToken() {
    if (this.getCsrfToken()) return this.getCsrfToken();
    try {
      const r = await fetch(`${this.baseUrl}/auth/csrf/`, { method: 'GET', credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        if (data && data.csrfToken) {
          this._cachedCsrfToken = data.csrfToken;
          return this._cachedCsrfToken;
        }
      }
    } catch (e) {
      logger.warn('Could not fetch CSRF token', { error: e.message });
    }
    return '';
  }

  /**
   * Convert camelCase to snake_case
   * Uses utility function from utilities/base
   */
  toSnakeCase(obj) {
    return toSnakeCase(obj);
  }

  /**
   * Convert snake_case to camelCase
   * Uses utility function from utilities/base
   */
  toCamelCase(obj) {
    return toCamelCase(obj);
  }

  // Restore isTokenExpired
  isTokenExpired(token) {
    if (!token) {
      logger.debug('No token provided');
      return true;
    }

    // Check if token is very recent - skip expiration check for fresh tokens
    if (this.tokenStorageTime && (Date.now() - this.tokenStorageTime) < 60000) { // 1 minute
      logger.debug('Token is fresh (less than 1 min old), assuming valid');
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expTime = payload.exp * 1000;
      const now = Date.now();
      const isExpired = expTime < now;
      
      logger.debug('Token expiration check', {
        userId: payload.user_id,
        isExpired,
        minutesUntilExpiry: Math.floor((expTime - now) / 1000 / 60)
      });
      
      return isExpired;
    } catch (e) {
      if (e.name === 'InvalidCharacterError' || e.message.includes('Failed to execute \'atob\'') || !token.includes('.')) {
        logger.error('Invalid JWT format, treating as expired', { error: e.message });
        return true; // Treat as expired to force refresh/relogin
      }
      logger.warn('JWT parsing failed, assuming token is valid', { error: e.message });
      return false;
    }
  }

  // Refresh token: backend reads refresh from httpOnly cookie (Phase 1). Access token in memory only (Phase 3).
  async refreshToken() {
    // If refresh is already in progress, await the existing promise
    if (this._refreshPromise) {
      logger.debug('Refresh already in progress, awaiting existing promise');
      try {
        await this._refreshPromise;
        // After existing refresh completes, return current access token
        return this.getAccessToken();
      } catch (error) {
        // If existing refresh failed, the promise has been cleared
        // Caller can retry or handle error
        throw error;
      }
    }

    if (this.refreshAttempts >= this.maxRefreshAttempts) {
      logger.error('Max refresh attempts reached, clearing tokens');
      this.clearAccessToken();
      throw new Error('Too many refresh attempts - tokens cleared');
    }

    this.refreshAttempts++;
    logger.debug('Token refresh attempt', { attempt: this.refreshAttempts });

    // Create and store the refresh promise
    this._refreshPromise = (async () => {
      try {
        await this.ensureCsrfToken();
        const csrf = this.getCsrfToken();
        const headers = { 'Content-Type': 'application/json' };
        if (csrf) headers['X-CSRFToken'] = csrf;

        const refreshUrl = this.getRefreshUrl();
        const response = await fetch(refreshUrl, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({})
        });

        let data = {};
        try {
          data = await response.json();
        } catch (_) {}
        
        if (response.ok && data && data.access) {
          this.setAccessToken(data.access);
          this.refreshAttempts = 0;
          logger.info('Token refreshed successfully');
          return data.access;
        }
        
        // Handle 401 - no refresh cookie / session expired / token revoked.
        // Distinct from "no in-memory access token yet" (that is expected on a fresh tab).
        if (response.status === 401) {
          this.refreshAttempts = this.maxRefreshAttempts; // Prevent further attempts this page load
          this.clearAccessToken({ resetRefreshAttempts: false });
          
          // Differentiate between expected and unexpected session expiry
          if (this.wasAuthenticatedBefore) {
            logger.warn('Session expired, user logged out', { hadToken: true });
            // Trigger session expired handler for logged-in users
            if (typeof window !== 'undefined' && window.adminManager) {
              window.adminManager.handleSessionExpired();
            }
          } else {
            logger.debug('No active session (expected for public users)');
          }
          
          throw new Error('Session expired');
        }
        
        const msg = data.detail || data.error || 'Invalid refresh response';
        throw new Error(msg);
      } catch (error) {
        // Only log as error if unexpected (not a simple session expiry for public users)
        if (error.message === 'Session expired' && !this.wasAuthenticatedBefore) {
          logger.debug('Session expired for public user');
        } else if (error.message !== 'Session expired') {
          logger.error('Token refresh failed unexpectedly', { error: error.message });
        }
        throw error;
      } finally {
        // Always clear the refresh promise when done
        this._refreshPromise = null;
      }
    })();

    return this._refreshPromise;
  }

  /**
   * Get current access token or refresh and return it. Use for account page / any flow that needs
   * to know "do we have a valid token?" without making a specific API call.
   * Missing in-memory token (fresh tab / consumed handoff) always attempts cookie refresh —
   * it must not short-circuit to "unauthenticated" without calling refreshToken().
   * @returns {Promise<string|null>} Access token or null if no session
   */
  async ensureValidToken() {
    let token = this.getAccessToken();
    if (token && !this.isTokenExpired(token)) return token;
    // No token or expired: always try refresh (httpOnly cookie) unless already exhausted
    try {
      token = await this.refreshToken();
      if (token) this.setAccessToken(token);
      return token || null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Shared admin auth bootstrap gate. Single source of truth for "is user authenticated for
   * admin dashboard?" Used by router, AdminManager, and route modules to avoid racing.
   * Deduplicates: multiple callers receive the same in-flight promise.
   * Fresh tabs have no sessionStorage handoff — rehydrate via peer BroadcastChannel and/or
   * refreshToken() (httpOnly cookie) before returning authenticated:false.
   * @returns {Promise<{ authenticated: boolean, user?: Object }>}
   */
  async awaitAdminAuth() {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const isAdminLoginPage = pathname.includes('admin-login');
    if (!this._isAdminContext || isAdminLoginPage) {
      return { authenticated: true }; // Not admin dashboard or on login page - no gate
    }
    if (this._adminAuthBootstrapPromise) {
      return this._adminAuthBootstrapPromise;
    }
    this._adminAuthBootstrapPromise = (async () => {
      try {
        // Brand-new tab: no in-memory token and no (or already consumed) handoff.
        // Reset refresh budget so a prior failed attempt cannot skip the first cookie refresh.
        if (!this.getAccessToken()) {
          this.refreshAttempts = 0;
          const peerToken = await this._requestTokenFromPeerTabs(250);
          if (peerToken && !this.isTokenExpired(peerToken)) {
            this.setAccessToken(peerToken, { broadcast: false });
          }
        }
        // Always attempt ensureValidToken (cookie refresh) when needed — do not treat
        // missing handoff alone as authenticated:false.
        const token = await this.ensureValidToken();
        if (!token) {
          this._adminAuthBootstrapPromise = null;
          return { authenticated: false };
        }
        const data = await this.request(`${this.baseUrl}/auth/admin/me/`, { _silent401: true });
        const user = (data && data.user) || (data && data.id ? data : null);
        const isAdmin = user && (user.is_admin === true || user.isAdmin === true);
        if (user && isAdmin) {
          return { authenticated: true, user: this.toCamelCase(user) };
        }
        this.clearAccessToken();
        this._adminAuthBootstrapPromise = null;
        return { authenticated: false };
      } catch (e) {
        if (e.status === 401 || e.status === 403) {
          this.clearAccessToken();
        }
        this._adminAuthBootstrapPromise = null;
        return { authenticated: false };
      }
    })();
    return this._adminAuthBootstrapPromise;
  }

  /**
   * Generate a unique key for request deduplication
   * Uses URL + method + body hash (for POST/PATCH/PUT)
   */
  _getRequestKey(url, method, body) {
    let key = `${method || 'GET'}:${url}`;
    if (body && (method === 'POST' || method === 'PATCH' || method === 'PUT')) {
      // For requests with body, include a hash of the body
      // Simple hash: just use JSON string (for exact matches)
      try {
        const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
        key += `:${bodyStr.length}`; // Use length as simple hash (faster than full hash)
      } catch (e) {
        // If body can't be stringified, include it in key
        key += `:${String(body)}`;
      }
    }
    return key;
  }

  /**
   * Make HTTP request with error handling and Authorization token
   * Includes request deduplication to prevent duplicate concurrent requests
   */
  async request(url, options = {}) {
    // In admin context, send whitelisted data paths to /api/admin/... (submissions, products, reports, analytics)
    let path = url.startsWith(this.baseUrl) ? url.slice(this.baseUrl.length) : (url.startsWith('/') ? url : '/' + url);
    if (this._isAdminContext && /^\/(submissions|products|reports|analytics|content)(\/|$)/.test(path)) {
      path = '/admin' + (path.startsWith('/') ? path : '/' + path);
      url = this.baseUrl + path;
    }

    const method = options.method || 'GET';
    const requestKey = this._getRequestKey(url, method, options.body);

    // Check if a request with the same key is already pending
    if (this.pendingRequests.has(requestKey)) {
      logger.debug('Duplicate request detected, returning existing Promise', { key: requestKey });
      return this.pendingRequests.get(requestKey);
    }

    // Create new request Promise
    const requestPromise = (async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          ...options.headers,
        };

        // Optional explicit token (e.g. from login response) so this request always has auth even if in-memory state was cleared
        const authTokenOverride = options.authToken;
        const omitAuthorization = options.omitAuthorization === true;
        let token = omitAuthorization
          ? null
          : (authTokenOverride !== undefined && authTokenOverride !== null
            ? authTokenOverride
            : this.getAccessToken());
        
        // Check if this is a public endpoint (skip auth for public endpoints)
        const isPublic = this.isPublicEndpoint(url);

        // When no explicit token override: check expiry and refresh or clear (skip when authTokenOverride is set)
        if (!authTokenOverride && (!token || this.isTokenExpired(token))) {
          // Skip token refresh only on actual login pages (admin-login or consumer login). Do NOT treat
          // admin dashboard (admin.html) as a login page — after full reload we have no in-memory token
          // and must attempt refresh from httpOnly cookie so the session survives Ctrl+R.
          const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
          const isLoginPage = pathname.includes('admin-login') || pathname.includes('login.html');
          
          if (isLoginPage || isPublic) {
            // On login pages or public endpoints, skip token refresh. Only clear when we have no token—
            // if we have a token (e.g. just after login), keep it so the next request (e.g. getUserProfile) can use it.
            if (!token) {
              this.clearAccessToken();
              token = null;
            }
          } else {
            // Only attempt refresh for non-public endpoints
            if (!isPublic) {
              logger.debug('Token expired, attempting refresh', { 
                url: url.replace(this.baseUrl, ''), 
                attempt: this.refreshAttempts 
              });
            }
            
            if (this.refreshAttempts < this.maxRefreshAttempts) {
              try {
                token = await this.refreshToken();
                if (token) this.setAccessToken(token);
              } catch (refreshError) {
                // Only error log for authenticated users or unexpected failures
                // Suppress errors for public endpoints (expected 401s)
                if (refreshError.message === 'Session expired' && !this.wasAuthenticatedBefore) {
                  logger.debug('Session expired for public user');
                } else if (!isPublic) {
                  logger.error('Token refresh failed', { error: refreshError.message, url: url.replace(this.baseUrl, '') });
                }
                this.clearAccessToken();
                token = null;
              }
            } else {
              if (!isPublic) {
                logger.warn('Max refresh attempts reached, proceeding without auth');
              }
              token = null;
            }
          }
        }

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          this.tokenStorageTime = Date.now(); // Update if we have a valid token
        }

        // Diagnostic logging (debug only)
        logger.debug('Making API request', {
          method,
          url: url.replace(this.baseUrl, ''), // Shorter URL
          hasAuth: !!token
        });

        // Determine timeout: longer for bot run, trusted search, products list, admin pending/submissions, and admin invite
        const isBotRunRequest = url.includes('/inventory-quotas/') && url.includes('/run/');
        const isTrustedSearchRequest = url.includes('/trusted-search');
        const isProductsListRequest = method === 'GET' && url.includes('/products') && !/\/products\/[^/]+\/?$/.test(url.replace(this.baseUrl, ''));
        const isAdminPendingRequest = url.includes('/products/pending/') ||
          (method === 'GET' && url.includes('/submissions'));
        const isAdminInviteRequest = url.includes('/auth/admin-invite/');
        const ADMIN_PENDING_TIMEOUT_MS = 15000;
        const PRODUCTS_LIST_TIMEOUT_MS = 20000;  // 20s for cold start / slow backend (homepage, dashboard)
        const ADMIN_INVITE_TIMEOUT_MS = 25000;    // 25s for admin invite (email send + cold start)
        const timeoutMs = options.timeout || (
          isBotRunRequest ? 60000 :  // 60s for bot runs
          isTrustedSearchRequest ? 30000 :  // 30s for trusted search (allows time for parallel API calls)
          isAdminPendingRequest ? ADMIN_PENDING_TIMEOUT_MS :  // 15s for admin pending/submissions load
          isProductsListRequest ? PRODUCTS_LIST_TIMEOUT_MS :  // 20s for /products/ list (homepage, etc.)
          isAdminInviteRequest ? ADMIN_INVITE_TIMEOUT_MS :  // 25s for admin invite (send email, cold start)
          5000  // 5s default
        );

        // Add timeout to prevent long waits when backend is unavailable (faster fallback to localStorage/demo items)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const doFetch = () => fetch(url, {
          ...options,
          credentials: 'include',
          headers: headers,
          signal: controller.signal,
        });

        let response;
        try {
          response = await doFetch();
          clearTimeout(timeoutId);
          return await this.handleResponse(response, options);
        } catch (fetchError) {
          clearTimeout(timeoutId);
          const isTimeout = fetchError.name === 'AbortError';
          // One retry for GET on timeout; one retry for admin-invite POST (cold start / email send)
          const mayRetry = isTimeout && !options._retried && (
            method === 'GET' || (method === 'POST' && url.includes('/auth/admin-invite/'))
          );
          if (mayRetry) {
            logger.warn('API request timeout, retrying once', { url: url.replace(this.baseUrl, ''), method });
            const retryController = new AbortController();
            const retryTimeoutId = setTimeout(() => retryController.abort(), timeoutMs);
            try {
              response = await fetch(url, { ...options, credentials: 'include', headers: headers, signal: retryController.signal, _retried: true });
              clearTimeout(retryTimeoutId);
              return await this.handleResponse(response, options);
            } catch (retryError) {
              clearTimeout(retryTimeoutId);
              if (retryError.name === 'AbortError') {
                throw new Error('Request timeout - backend unavailable');
              }
              throw retryError;
            }
          }
          if (isTimeout) {
            throw new Error('Request timeout - backend unavailable');
          }
          throw fetchError;
        }
      } catch (error) {
        // 401 on admin/me when no session (e.g. admin-login page) is expected; don't log as error
        const silent401 = options._silent401 && error.status === 401;
        if (!silent401) {
          logger.error('API request failed', { 
            error: error.message, 
            url: url.replace(this.baseUrl, ''),
            method 
          });
        }
        throw error;
      } finally {
        // Always remove from pending requests map when done (success or error)
        this.pendingRequests.delete(requestKey);
      }
    })();

    // Store the promise in the map
    this.pendingRequests.set(requestKey, requestPromise);

    return requestPromise;
  }

  /**
   * Turn a DRF field-error body into a readable message.
   * DRF returns per-field validation errors as { field: ["msg", ...] } with no
   * detail/message/error key, so without this a 400 reads as a bare status code.
   * @param {Object} errorData - parsed error response body
   * @returns {string} "field: msg" pairs joined by '; ', or '' when the body is not field errors
   */
  formatFieldErrors(errorData) {
    if (!errorData || typeof errorData !== 'object' || Array.isArray(errorData)) return '';
    const parts = [];
    for (const [field, value] of Object.entries(errorData)) {
      const messages = Array.isArray(value) ? value : [value];
      const text = messages
        .filter((m) => typeof m === 'string' || typeof m === 'number')
        .join(' ');
      if (!text) continue;
      // non_field_errors carries no useful field name
      parts.push(field === 'non_field_errors' ? text : `${field}: ${text}`);
    }
    return parts.join('; ');
  }

  async handleResponse(response, options = {}) {
    try {

      if (!response.ok) {
        // Try to read error response body, but handle cases where it can't be read
        let errorData = {};
        try {
          // Clone the response before reading to avoid stream consumption issues
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const text = await response.text();
            if (text) {
              errorData = JSON.parse(text);
            }
          } else {
            // For non-JSON responses, try to get text
            const text = await response.text();
            if (text) {
              errorData = { message: text, detail: text };
            }
          }
        } catch (parseError) {
          // If we can't parse the error response, use a generic message
          logger.warn('Could not parse error response', { error: parseError.message, status: response.status });
          errorData = { 
            message: `Server error (${response.status})`,
            detail: `HTTP ${response.status} ${response.statusText}`
          };
        }
        
        // Special handling for 409 Conflict responses
        if (response.status === 409) {
          const conflictError = new Error(errorData.message || errorData.error || 'Conflict detected');
          conflictError.status = 409;
          conflictError.isConflict = true;
          conflictError.conflictData = errorData; // Full conflict response (error, message, conflict, current_data)
          throw conflictError;
        }
        
        // Phase 3: clear in-memory token on 401 so next request will try refresh or re-login
        if (response.status === 401) {
          this.clearAccessToken();
          // In consumer context, clear user display cache so UI does not show stale "logged in" state
          if (!this._isAdminContext) {
            localStorage.removeItem('usedfindr_user');
            localStorage.removeItem('usedfindr_finds');
            try {
              window.dispatchEvent(new CustomEvent('auth:sessionInvalidated'));
            } catch (_) {}
          }
        }

        // Special handling for 403 Forbidden - likely authentication issue
        if (response.status === 403) {
          const authError = new Error(errorData.detail || errorData.message || errorData.error || 'Authentication credentials were not provided.');
          authError.status = 403;
          authError.isAuthError = true;
          authError.response = errorData;
          authError.body = errorData;
          // Log helpful message for debugging
          logger.warn('Authentication failed - may need to log in again');
          throw authError;
        }
        
        // Special handling for 429 Too Many Requests (Rate Limit)
        if (response.status === 429) {
          const rateLimitError = new Error(errorData.detail || errorData.message || errorData.error || 'Rate limit exceeded. Please try again later.');
          rateLimitError.status = 429;
          rateLimitError.isRateLimit = true;
          rateLimitError.response = errorData;
          rateLimitError.body = errorData;
          rateLimitError.data = errorData; // Alias so retry_after etc. are available as error.data.retry_after
          // Include response headers for rate limit information
          rateLimitError.headers = {
            get: (name) => response.headers.get(name),
            'Retry-After': response.headers.get('Retry-After'),
            'X-RateLimit-Limit': response.headers.get('X-RateLimit-Limit'),
            'X-RateLimit-Remaining': response.headers.get('X-RateLimit-Remaining'),
            'X-RateLimit-Reset': response.headers.get('X-RateLimit-Reset')
          };
          logger.warn('Rate limit exceeded (429)', { 
            remaining: errorData.rateLimitRemaining,
            reset: errorData.rateLimitReset 
          });
          throw rateLimitError;
        }
        
        // Special handling for 500 Internal Server Error
        if (response.status === 500) {
          const serverError = new Error(errorData.detail || errorData.message || errorData.error || 'Internal server error. Please try again later.');
          serverError.status = 500;
          serverError.response = errorData;
          serverError.body = errorData;
          logger.error('Server error (500)', { error: errorData });
          throw serverError;
        }
        
        // Create error with status code and full error data
        const errorMessage = errorData.detail || errorData.message || errorData.error
          || this.formatFieldErrors(errorData)
          || `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = errorData; // Include full error response for debugging
        error.body = errorData; // Alias for compatibility
        
        // Log full error details for 400 and 503 errors (validation errors and service unavailable)
        if (response.status === 400 || response.status === 503) {
          logger.error(`Error (${response.status})`, { errorData });
        }
        
        throw error;
      }

      // Handle empty responses (including DELETE requests)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        // Handle empty JSON response (some DELETE operations may return empty body)
        if (!text || text.trim() === '') {
          return {};
        }
        try {
          return JSON.parse(text);
        } catch (e) {
          logger.warn('Failed to parse JSON response', { error: e.message });
          return {};
        }
      }
      // For non-JSON responses (like 204 No Content), return empty object
      return {};
    } catch (error) {
      // 401 when no session (e.g. admin-login) is expected; don't log as error if caller asked for silent
      const silent401 = options._silent401 && error.status === 401;
      if (!silent401) {
        logger.error('handleResponse failed', { error: error.message });
      }
      throw error;
    }
  }

  /**
   * Build query string from params object.
   * Array values are sent as repeated keys (e.g. category: [1,2] -> category=1&category=2).
   */
  buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) return '';
    const queryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            if (v !== null && v !== undefined && v !== '') queryParams.append(key, v);
          });
        } else {
          queryParams.append(key, value);
        }
      }
    }
    return queryParams.toString();
  }

  /**
   * Build full URL with admin path rewrite when in admin context (for blob/download requests).
   */
  _buildFullUrl(url) {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    let path = fullUrl.startsWith(this.baseUrl) ? fullUrl.slice(this.baseUrl.length) : (url.startsWith('/') ? url : '/' + url);
    if (this._isAdminContext && /^\/(submissions|products|reports|analytics|content)(\/|$)/.test(path)) {
      path = '/admin' + (path.startsWith('/') ? path : '/' + path);
    }
    return `${this.baseUrl}${path}`;
  }

  /**
   * GET request that returns the response as a Blob (e.g. for file export).
   * Uses same auth and URL rewriting as request().
   */
  async getBlob(url, options = {}) {
    const fullUrl = this._buildFullUrl(url);
    const headers = { ...options.headers };
    const token = this.getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(fullUrl, { method: 'GET', credentials: 'include', headers });
    if (!response.ok) {
      const text = await response.text();
      const err = new Error(text || `Export failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return response.blob();
  }

  /**
   * POST JSON and return response as Blob (e.g. ZIP export).
   */
  async postBlob(url, data = {}, options = {}) {
    const fullUrl = this._buildFullUrl(url);
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };
    const token = this.getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let message = `Export failed: ${response.status}`;
      try {
        const errJson = await response.json();
        message = errJson.error || errJson.detail || message;
      } catch (_) {
        const text = await response.text();
        if (text) message = text;
      }
      const err = new Error(message);
      err.status = response.status;
      throw err;
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    blob._downloadFilename = match ? match[1] : null;
    return blob;
  }

  /**
   * Convenience method for GET requests
   */
  async get(url, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    return this.request(fullUrl, { ...options, method: 'GET' });
  }

  /**
   * Convenience method for POST requests
   */
  async post(url, data = {}, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    return this.request(fullUrl, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Convenience method for PATCH requests
   */
  async patch(url, data = {}, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    return this.request(fullUrl, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Convenience method for PUT requests
   */
  async put(url, data = {}, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    return this.request(fullUrl, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Convenience method for DELETE requests
   */
  async delete(url, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    return this.request(fullUrl, { ...options, method: 'DELETE' });
  }

  // ==================== PRODUCTS ====================

  /**
   * Get all products with optional filtering
   * @param {Object} params - Query parameters (status, category, condition, search)
   * @returns {Promise<Array>} Array of products
   */
  async getProducts(params = {}) {
    try {
      const queryString = this.buildQueryString(params);
      const url = `${this.baseUrl}/products/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      
      // Handle paginated response (DRF returns {count, next, previous, results: [...]})
      // or plain array response
      let products = [];
      if (data) {
        if (Array.isArray(data)) {
          products = data;
        } else if (data.results && Array.isArray(data.results)) {
          products = data.results;
        } else {
          products = [];
        }
      }

      // How the set divided at the trust floor, when one was asked for:
      // {floor, total, clears, below, unrated}. Stashed rather than returned
      // so the signature stays "an array of products" for every caller. The
      // empty state needs it -- a filtered feed of zero has to be able to say
      // whether those sellers failed or were simply unrated. Cleared on a
      // response that carried none, so it can never describe an older fetch.
      this.lastTrustFloor = (data && !Array.isArray(data) && data.trust_floor) || null;

      return this.toCamelCase(products);
    } catch (error) {
      logger.error('Error fetching products', { error: error.message });
      throw error;
    }
  }

  /**
   * Get a single product by ID
   * @param {string} productId - Product ID
   * @returns {Promise<Object>} Product object
   */
  async getProduct(productId) {
    try {
      const encodedId = encodeURIComponent(productId);
      const data = await this.request(`${this.baseUrl}/products/${encodedId}/`);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching product', { error: error.message });
      throw error;
    }
  }

  async getProductByNormalized(normalizedId) {
    if (!normalizedId) throw new Error('normalizedId required');
    return await this.request(`${this.baseUrl}/products/by-normalized/${normalizedId}/`);
  }

  /**
   * Create a new product
   * @param {Object} productData - Product data in camelCase
   * @returns {Promise<Object>} Created product
   */
  async createProduct(productData) {
    try {
      // Convert to snake_case for API
      const apiData = this.toSnakeCase(productData);
      
      // Ensure required fields
      if (!apiData.product_id) {
        // Generate product_id if not provided
        apiData.product_id = productData.productId || `product_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      
      // Convert addedAt to added_at if present
      if (productData.addedAt && !apiData.added_at) {
        apiData.added_at = productData.addedAt;
      }
      
      // Set default status if not provided
      if (!apiData.status) {
        apiData.status = 'active';
      }

      const data = await this.request(`${this.baseUrl}/products/`, {
        method: 'POST',
        body: JSON.stringify(apiData),
      });
      
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error creating product', { error: error.message });
      if (this.useLocalStorageFallback) {
        return this.saveProductToLocalStorage(productData);
      }
      throw error;
    }
  }

  /**
   * Update a product
   * @param {string} productId - Product ID
   * @param {Object} productData - Partial product data in camelCase
   * @returns {Promise<Object>} Updated product
   */
  async updateProduct(productId, productData) {
    try {
      const apiData = this.toSnakeCase(productData);
      const encodedId = encodeURIComponent(productId);
      const data = await this.request(`${this.baseUrl}/products/${encodedId}/`, {
        method: 'PATCH',
        body: JSON.stringify(apiData),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error updating product', { error: error.message });
      // If it's a conflict error, ensure it's properly re-thrown with all properties
      if (error.isConflict === true || error.status === 409) {
        throw error; // Re-throw conflict error with all properties intact
      }
      throw error;
    }
  }

  /**
   * Delete a product
   * @param {string} productId - Product ID
   * @returns {Promise<void>}
   */
  async deleteProduct(productId) {
    try {
      await this.request(`${this.baseUrl}/products/${productId}/`, {
        method: 'DELETE',
      });
    } catch (error) {
      logger.error('Error deleting product', { error: error.message });
      throw error;
    }
  }

  /**
   * Soft-remove a listing from the public site (Inventory / Cowork).
   * @param {string} productId - ebay_* or Product.product_id
   * @param {{ dryRun?: boolean }} [options]
   */
  async removeFromSite(productId, options = {}) {
    const body = { id: productId, action: 'remove' };
    if (options.dryRun) body.dry_run = true;
    const data = await this.post('/products/admin/remove-from-site/', body);
    return this.toCamelCase(data);
  }

  /**
   * Restore a soft-removed listing to the public site.
   * @param {string} productId
   * @param {{ dryRun?: boolean }} [options]
   */
  async restoreToSite(productId, options = {}) {
    const body = { id: productId, action: 'restore' };
    if (options.dryRun) body.dry_run = true;
    const data = await this.post('/products/admin/remove-from-site/', body);
    return this.toCamelCase(data);
  }

  /**
   * Archive or restore a MasterParent (whole group off/on site).
   * @param {number|string} parentId
   * @param {'archive'|'restore'} action
   * @param {{ dryRun?: boolean }} [options]
   */
  async archiveParent(parentId, action = 'archive', options = {}) {
    const body = { id: parentId, action };
    if (options.dryRun) body.dry_run = true;
    const data = await this.post('/products/admin/archive-parent/', body);
    return this.toCamelCase(data);
  }

  /**
   * Bulk admin command ingestion (Cowork / Commands panel).
   * @param {{ dryRun?: boolean, autonomous?: boolean, confirmLargeBatch?: boolean, idempotencyKey?: string, commands: Array<object> }} payload
   */
  async runAdminCommands(payload = {}) {
    const body = {
      dry_run: !!payload.dryRun,
      commands: payload.commands || [],
    };
    if (payload.autonomous) body.autonomous = true;
    if (payload.confirmLargeBatch) body.confirm_large_batch = true;
    if (payload.idempotencyKey) body.idempotency_key = payload.idempotencyKey;
    // /products/admin/commands/ falls to request()'s 5s default, which is fine
    // for the audits and fatal for anything that spends its own wall clock:
    // mgmt.resight_deep_stock runs to a 15s server budget, so the client
    // aborted every single call while the run completed server-side and the
    // console reported a backend outage that had not happened.
    const options = payload.timeout ? { timeout: payload.timeout } : {};
    const data = await this.post('/products/admin/commands/', body, options);
    return this.toCamelCase(data);
  }

  // ==================== REPORTS ====================

  /**
   * Get all reports with optional filtering
   * @param {Object} params - Query parameters (status, product_id, reason)
   * @returns {Promise<Array>} Array of reports
   */
  async getReports(params = {}) {
    try {
      const queryString = this.buildQueryString(params);
      const url = `${this.baseUrl}/reports/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      
      // Handle paginated response (DRF returns {count, next, previous, results: [...]})
      // or plain array response
      let reports = [];
      if (data) {
        if (Array.isArray(data)) {
          reports = data;
        } else if (data.results && Array.isArray(data.results)) {
          reports = data.results;
        } else {
          reports = [];
        }
      }
      
      return this.toCamelCase(reports);
    } catch (error) {
      logger.error('Error fetching reports', { error: error.message });
      throw error;
    }
  }

  /**
   * Get a single report by ID
   * @param {string|number} reportId - Report ID
   * @returns {Promise<Object>} Report object
   */
  async getReport(reportId) {
    try {
      const data = await this.request(`${this.baseUrl}/reports/${reportId}/`);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching report', { error: error.message });
      throw error;
    }
  }

  /**
   * Create a new report
   * @param {Object} reportData - Report data in camelCase
   * @returns {Promise<Object>} Created report
   */
  async createReport(reportData) {
    try {
      const apiData = this.toSnakeCase(reportData);
      
      // Ensure required fields
      if (!apiData.status) {
        apiData.status = 'pending';
      }

      const data = await this.request(`${this.baseUrl}/reports/`, {
        method: 'POST',
        body: JSON.stringify(apiData),
      });
      
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error creating report', { error: error.message });
      if (this.useLocalStorageFallback) {
        return this.saveReportToLocalStorage(reportData);
      }
      throw error;
    }
  }

  /**
   * Update a report
   * @param {string|number} reportId - Report ID
   * @param {Object} reportData - Partial report data in camelCase
   * @returns {Promise<Object>} Updated report
   */
  async updateReport(reportId, reportData) {
    try {
      const apiData = this.toSnakeCase(reportData);
      // Debug: Log what we're sending for conflict detection
      if (apiData.updated_at) {
        logger.debug('Sending updated_at for conflict detection:', apiData.updated_at);
      } else {
        logger.warn('WARNING: updated_at not included in request data!');
      }
      logger.debug('Full request data:', JSON.stringify(apiData, null, 2));
      
      const data = await this.request(`${this.baseUrl}/reports/${reportId}/`, {
        method: 'PATCH',
        body: JSON.stringify(apiData),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error updating report', { error: error.message });
      // If it's a conflict error, ensure it's properly re-thrown with all properties
      if (error.isConflict === true || error.status === 409) {
        logger.debug('Conflict detected!', error.conflictData);
        throw error; // Re-throw conflict error with all properties intact
      }
      throw error;
    }
  }

  // ==================== HISTORY ====================

  /**
   * Get history for a report
   * @param {string|number} reportId - Report ID
   * @param {Object} params - Query parameters (start_date, end_date, history_type)
   * @returns {Promise<Array>} Array of historical records
   */
  async getReportHistory(reportId, params = {}) {
    try {
      const queryString = this.buildQueryString(params);
      const url = `${this.baseUrl}/reports/${reportId}/history/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      
      // Handle paginated response (DRF returns {count, next, previous, results: [...]})
      // or plain array response
      let history = [];
      if (data) {
        if (Array.isArray(data)) {
          history = data;
        } else if (data.results && Array.isArray(data.results)) {
          history = data.results;
        } else {
          history = [];
        }
      }
      
      return this.toCamelCase(history);
    } catch (error) {
      logger.error('Error fetching report history', { error: error.message });
      // Return empty array on error to allow UI to show empty state
      return [];
    }
  }

  /**
   * Get a specific historical version of a report
   * @param {string|number} reportId - Report ID
   * @param {string|number} historyId - History record ID
   * @returns {Promise<Object>} Historical record
   */
  async getReportHistoryDetail(reportId, historyId) {
    try {
      const data = await this.request(`${this.baseUrl}/reports/${reportId}/history/${historyId}/`);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching report history detail', { error: error.message });
      throw error;
    }
  }

  /**
   * Get history for a product
   * @param {string|number} productId - Product ID
   * @param {Object} params - Query parameters (start_date, end_date, history_type)
   * @returns {Promise<Array>} Array of historical records
   */
  async getProductHistory(productId, params = {}) {
    try {
      const queryString = this.buildQueryString(params);
      const url = `${this.baseUrl}/products/${productId}/history/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      
      // Handle paginated response (DRF returns {count, next, previous, results: [...]})
      // or plain array response
      let history = [];
      if (data) {
        if (Array.isArray(data)) {
          history = data;
        } else if (data.results && Array.isArray(data.results)) {
          history = data.results;
        } else {
          history = [];
        }
      }
      
      return this.toCamelCase(history);
    } catch (error) {
      logger.error('Error fetching product history', { error: error.message });
      // Return empty array on error to allow UI to show empty state
      return [];
    }
  }

  /**
   * Get a specific historical version of a product
   * @param {string|number} productId - Product ID
   * @param {string|number} historyId - History record ID
   * @returns {Promise<Object>} Historical record
   */
  async getProductHistoryDetail(productId, historyId) {
    try {
      const data = await this.request(`${this.baseUrl}/products/${productId}/history/${historyId}/`);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching product history detail', { error: error.message });
      throw error;
    }
  }

  // ==================== SUBMISSIONS ====================

  /**
   * Get all submissions with optional filtering
   * @param {Object} params - Query parameters (review_status, user_id, category)
   * @returns {Promise<Array>} Array of submissions
   */
  async getSubmissions(params = {}) {
    try {
      const queryString = this.buildQueryString(params);
      const url = `${this.baseUrl}/submissions/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      
      // Handle paginated response (DRF returns {count, next, previous, results: [...]}),
      // grouped response ({ user_submissions, trusted_finder_submissions, bot_discoveries }), or plain array
      let submissions = [];
      if (data) {
        if (Array.isArray(data)) {
          submissions = data;
        } else if (data.results && Array.isArray(data.results)) {
          submissions = data.results;
        } else if (data.user_submissions || data.trusted_finder_submissions || data.bot_discoveries) {
          const userSubs = Array.isArray(data.user_submissions) ? data.user_submissions : [];
          const trustedSubs = Array.isArray(data.trusted_finder_submissions) ? data.trusted_finder_submissions : [];
          const botSubs = Array.isArray(data.bot_discoveries) ? data.bot_discoveries : [];
          submissions = [...userSubs, ...trustedSubs, ...botSubs];
        } else {
          submissions = [];
        }
      }
      
      return this.toCamelCase(submissions);
    } catch (error) {
      logger.error('Error fetching submissions', { error: error.message });
      throw error;
    }
  }

  /**
   * Get a single submission by ID
   * @param {string|number} submissionId - Submission ID
   * @returns {Promise<Object>} Submission object
   */
  async getSubmission(submissionId) {
    try {
      const data = await this.request(`${this.baseUrl}/submissions/${submissionId}/`);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching submission', { error: error.message });
      throw error;
    }
  }

  /**
   * Create a new submission
   * @param {Object} submissionData - Submission data in camelCase
   * @returns {Promise<Object>} Created submission
   */
  async createSubmission(submissionData) {
    try {
      const apiData = this.toSnakeCase(submissionData);
      
      // Map reviewStatus to review_status if present
      if (submissionData.reviewStatus && !apiData.review_status) {
        apiData.review_status = submissionData.reviewStatus;
      }
      
      // Ensure default review_status
      if (!apiData.review_status) {
        apiData.review_status = 'pending';
      }

      const data = await this.request(`${this.baseUrl}/submissions/`, {
        method: 'POST',
        body: JSON.stringify(apiData),
      });
      
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error creating submission', { error: error.message });
      // No localStorage fallback - guests can now submit directly via API
      throw error;
    }
  }

  /**
   * A5b: bulk Amazon used-seed import (admin).
   * @param {{ rows: Array, mode?: 'submission'|'publish' }} payload
   */
  async bulkAmazonSeed(payload) {
    const body = {
      rows: payload.rows || [],
      mode: payload.mode || 'submission',
    };
    const data = await this.request(`${this.baseUrl}/products/amazon/bulk-seed/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return data;
  }

  /**
   * Update a submission
   * @param {string|number} submissionId - Submission ID
   * @param {Object} submissionData - Partial submission data in camelCase
   * @returns {Promise<Object>} Updated submission
   */
  async updateSubmission(submissionId, submissionData) {
    try {
      const apiData = this.toSnakeCase(submissionData);
      
      // Map reviewStatus to review_status if present
      if (submissionData.reviewStatus && !apiData.review_status) {
        apiData.review_status = submissionData.reviewStatus;
      }

      const data = await this.request(`${this.baseUrl}/submissions/${submissionId}/`, {
        method: 'PATCH',
        body: JSON.stringify(apiData),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error updating submission', { error: error.message });
      throw error;
    }
  }

  // ==================== AUTHENTICATION ====================

  /**
   * Login user
   * @param {string} username - Username or email
   * @param {string} password - Password
   * @param {boolean} rememberMe - Whether to extend token expiration (30 days for admin, 7 days for consumer)
   * @param {boolean} isAdminLogin - Whether this is an admin login (default: false)
   * @returns {Promise<Object>} User object with success status
   */
  async login(usernameOrEmail, password, rememberMe = false, isAdminLogin = false) {
    try {
      await this.ensureCsrfToken();
      const csrf = this.getCsrfToken();
      const headers = { 'Content-Type': 'application/json' };
      if (csrf) headers['X-CSRFToken'] = csrf;

      const loginUrl = isAdminLogin
        ? `${this.baseUrl}/auth/admin/token/`
        : `${this.baseUrl}/auth/token/`;
      logger.info('[Admin login] POST %s (isAdminLogin=%s)', loginUrl, isAdminLogin);
      const response = await fetch(loginUrl, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          username_or_email: usernameOrEmail,
          password: password,
          remember_me: rememberMe,
          is_admin_login: isAdminLogin
        }),
      });
      let data = {};
      try {
        data = await response.json();
      } catch (_) {}
      if (!response.ok) {
        const err = new Error(data.detail || data.error || data.message || `HTTP ${response.status}`);
        err.status = response.status;
        err.response = data;
        throw err;
      }

      const keys = data ? Object.keys(data) : [];
      logger.info('[Admin login] Response: keys=%s, totp_required=%s, has_access=%s', keys, !!data?.totp_required, !!data?.access);

      // When 2FA is enabled, backend returns totp_required + temp_token (no access yet)
      if (data && data.totp_required && data.temp_token) {
        logger.info('Admin login requires TOTP step');
        return data;
      }

      // Backend sets refresh in httpOnly cookie; access token in memory only (Phase 3)
      if (data && data.access) {
        this.setAccessToken(data.access);
        if (data.user) {
          localStorage.setItem('usedfindr_user', JSON.stringify(data.user));
        }
      }

      logger.info('Login successful');
      return data;
    } catch (error) {
      const errorMessage = error.message || String(error) || 'Unknown error occurred during login';
      logger.error('Error in login method', {
        message: errorMessage,
        stack: error.stack
      });
      if (!error.message) {
        error.message = errorMessage;
      }
      throw error;
    }
  }

  /**
   * Register a new user
   * @param {Object} userData - User data (firstName, lastName, email, password)
   * @returns {Promise<Object>} User object with success status
   */
  async register(userData) {
    try {
      const registerUrl = `${this.baseUrl}/auth/register/`;
      const requestBody = {
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        password: userData.password,
        newsletter: userData.newsletter || false,
        terms_accepted: !!userData.terms
      };
      
      const data = await this.request(registerUrl, {
          method: 'POST',
          body: JSON.stringify(requestBody),
          timeout: 15000  // 15 seconds - enough time for user creation even if email is slow
      });
      
      if (data && data.access) {
        this.setAccessToken(data.access);
        if (data.user) {
          localStorage.setItem('usedfindr_user', JSON.stringify(data.user));
        }
      }

      logger.info('Registration successful');
      return data;
    } catch (error) {
      const errorMessage = error.message || String(error) || 'Unknown error occurred during registration';
      logger.error('Error in registration method', {
        message: errorMessage,
        stack: error.stack
      });
      if (!error.message) {
        error.message = errorMessage;
      }
      throw error;
    }
  }

  /**
   * Logout user
   * @returns {Promise<Object>} Success response
   */
  async logout() {
    try {
      await this.ensureCsrfToken();
      const csrf = this.getCsrfToken();
      const headers = { 'Content-Type': 'application/json' };
      if (csrf) headers['X-CSRFToken'] = csrf;

      // Blacklist: backend reads refresh from httpOnly cookie; use correct endpoint for context
      const blacklistUrl = this._isAdminContext
        ? `${this.baseUrl}/auth/admin/token/blacklist/`
        : `${this.baseUrl}/auth/token/blacklist/`;
      await fetch(blacklistUrl, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({})
      }).catch(() => {});

      this.clearAccessToken();

      return { success: true };
    } catch (error) {
      logger.error('Error logging out', { error: error.message });
      this.clearAccessToken();
      throw error;
    }
  }

  /**
   * Check if user is authenticated with Django
   * In admin context we bypass token/verify (consumer-only); session is validated by subsequent ensureValidToken/getCurrentAdmin (admin/me).
   * @returns {Promise<boolean>} True if authenticated
   */
  async checkAuth() {
    try {
      // Phase 3: token from memory; refresh uses httpOnly cookie
      let token = this.getAccessToken();
      if (!token || this.isTokenExpired(token)) {
        try {
          token = await this.refreshToken();
          if (token) this.setAccessToken(token);
        } catch (_) {
          return false;
        }
      }
      token = this.getAccessToken();
      if (!token) return false;

      // Admin context: do not call consumer token/verify (aud=consumer); admin tokens have aud=admin and would be rejected.
      // Session validity is validated by subsequent ensureValidToken() and getCurrentAdmin() (admin/me); 401 will clear token and redirect.
      if (this._isAdminContext) {
        return true;
      }

      // Consumer context: verify token with SimpleJWT (POST body { "token": "<access_token>" })
      await this.request(`${this.baseUrl}/auth/token/verify/`, {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      return true;
    } catch (error) {
      logger.debug('Auth check failed (expected for non-authenticated users)', { error: error.message });
      this.clearAccessToken();
      return false;
    }
  }

  /**
   * Get current authenticated user
   * @returns {Promise<Object>} User object or null
   */
  async getCurrentUser() {
    try {
      const userUrl = this._isAdminContext
        ? `${this.baseUrl}/auth/admin/me/`
        : `${this.baseUrl}/auth/user/`;
      const data = await this.request(userUrl, { _silent401: this._isAdminContext });
      return this.toCamelCase(data);
    } catch (error) {
      // 401/403 on admin context (e.g. admin-login page) is expected when no one is logged in; don't log as error
      if (error.status === 401 || error.status === 403) {
        this.clearAccessToken();
        if (this._isAdminContext) {
          logger.debug('No admin session (401/403)', { status: error.status });
        } else {
          logger.error('Error fetching current user', { error: error.message });
        }
        return { user: null };
      }
      logger.error('Error fetching current user', { error: error.message });
      throw error;
    }
  }

  /**
   * Verify admin TOTP or backup code after password step (when login returned totp_required).
   * On success: backend sets admin refresh cookie; we set access token and return { access, user }.
   * @param {string} tempToken - From login response
   * @param {string} code - 6-digit TOTP or backup code
   * @returns {Promise<Object>} { access, user }
   */
  async verifyAdminTotp(tempToken, code, rememberHours = 0) {
    await this.ensureCsrfToken();
    const csrf = this.getCsrfToken();
    const headers = { 'Content-Type': 'application/json' };
    if (csrf) headers['X-CSRFToken'] = csrf;
    const body = { temp_token: tempToken, code: (code || '').trim() };
    if (rememberHours !== undefined && rememberHours !== null) {
      const h = parseInt(rememberHours, 10);
      if ([0, 24, 720].includes(h)) body.remember_hours = h;
    }
    const response = await fetch(`${this.baseUrl}/auth/admin/token/verify-totp/`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
    let data = {};
    try {
      data = await response.json();
    } catch (_) {}
    if (!response.ok) {
      const err = new Error(data.detail || data.error || data.message || `HTTP ${response.status}`);
      err.status = response.status;
      err.response = data;
      throw err;
    }
    if (data && data.access) {
      this.setAccessToken(data.access);
      if (data.user) {
        localStorage.setItem('usedfindr_user', JSON.stringify(data.user));
      }
    }
    return data;
  }

  /**
   * GET admin 2FA status. Requires admin JWT.
   * @returns {Promise<{ enabled: boolean, backupCodesCount: number }>}
   */
  async getAdmin2faStatus() {
    const data = await this.request(`${this.baseUrl}/auth/admin/2fa/status/`);
    return this.toCamelCase(data);
  }

  /**
   * POST admin 2FA setup. Returns secret, provisioningUri, qrDataUrl.
   * @returns {Promise<Object>}
   */
  async admin2faSetup() {
    return this.request(`${this.baseUrl}/auth/admin/2fa/setup/`, { method: 'POST' });
  }

  /**
   * POST admin 2FA confirm with first TOTP code. Returns backup_codes.
   * @param {string} code - 6-digit code from app
   * @returns {Promise<Object>}
   */
  async admin2faConfirm(code) {
    const data = await this.request(`${this.baseUrl}/auth/admin/2fa/confirm/`, {
      method: 'POST',
      body: JSON.stringify({ code: (code || '').trim() }),
    });
    return this.toCamelCase(data);
  }

  /**
   * POST admin 2FA disable. Requires password and current TOTP/backup code.
   * @param {string} password
   * @param {string} code
   * @returns {Promise<Object>}
   */
  async admin2faDisable(password, code) {
    const data = await this.request(`${this.baseUrl}/auth/admin/2fa/disable/`, {
      method: 'POST',
      body: JSON.stringify({
        password: password || '',
        code: (code || '').trim(),
      }),
    });
    return this.toCamelCase(data);
  }

  /**
   * GET admin 2FA preference (remember duration: 0, 24, 720).
   * @returns {Promise<{ rememberHours: number }>}
   */
  async getAdmin2faPreference() {
    const data = await this.request(`${this.baseUrl}/auth/admin/2fa/preference/`);
    return this.toCamelCase(data);
  }

  /**
   * PATCH admin 2FA preference. rememberHours: 0=every login, 24=24h, 720=30 days.
   * @param {number} rememberHours
   * @returns {Promise<{ rememberHours: number }>}
   */
  async updateAdmin2faPreference(rememberHours) {
    const h = [0, 24, 720].includes(parseInt(rememberHours, 10)) ? parseInt(rememberHours, 10) : 0;
    const data = await this.request(`${this.baseUrl}/auth/admin/2fa/preference/`, {
      method: 'PATCH',
      body: JSON.stringify({ rememberHours: h }),
    });
    return this.toCamelCase(data);
  }

  /**
   * Get user profile with reputation, level, stats, and badges
   * @param {string} [accessTokenOverride] - If provided (e.g. from login response), use for this request only so profile always has auth
   * @returns {Promise<Object>} UserProfile object
   */
  async getUserProfile(accessTokenOverride) {
    try {
      const url = `${this.baseUrl}/auth/users/profile/?ts=${Date.now()}`;
      const opts = { cache: 'no-store' };
      if (accessTokenOverride) opts.authToken = accessTokenOverride;
      const data = await this.request(url, opts);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching user profile', { error: error.message });
      // Return null if not authenticated (401/403)
      if (error.message.includes('401') || error.message.includes('403')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get list of all available badges
   * @returns {Promise<Array>} Array of Badge objects
   */
  async getBadges() {
    try {
      const data = await this.request(`${this.baseUrl}/auth/badges/`);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching badges', { error: error.message });
      throw error;
    }
  }

  /**
   * Get user's submissions
   * @param {string|number} userId - User ID
   * @returns {Promise<Array>} Array of submission objects
   */
  async getUserSubmissions(userId) {
    try {
      const data = await this.getSubmissions({ user_id: userId });
      return data;
    } catch (error) {
      logger.error('Error fetching user submissions', { error: error.message });
      throw error;
    }
  }

  /**
   * Request password reset email. No auth required.
   * @param {string} email - User email
   * @returns {Promise<Object>} Response with detail message
   */
  async requestPasswordReset(email) {
    const data = await this.request(`${this.baseUrl}/auth/password-reset/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    return data;
  }

  /**
   * Confirm password reset with token. No auth required.
   * @param {string} uidb64 - Base64-encoded user id
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Response with detail or error
   */
  async confirmPasswordReset(uidb64, token, newPassword) {
    const data = await this.request(`${this.baseUrl}/auth/password-reset/confirm/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uidb64, token, new_password: newPassword }),
    });
    return data;
  }

  /**
   * Get available profile icons
   * @returns {Promise<Array>} Array of available icon emojis/identifiers
   */
  async getProfileIcons() {
    try {
      const data = await this.request(`${this.baseUrl}/auth/profile-icons/`, {
        method: 'GET',
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching profile icons', { error: error.message });
      throw error;
    }
  }

  /**
   * Update admin console UI preferences (e.g. discovery quota table filters/sort).
   * Admin context only. Payload is merged into stored preferences (e.g. { discoveryQuotaTable: { searchQuery, statusFilter, sortBy, autoFilter } }).
   * @param {Object} payload - Preferences to merge (camelCase keys preserved)
   * @returns {Promise<Object>} { adminUiPreferences }
   */
  async updateAdminPreferences(payload) {
    try {
      const data = await this.request(`${this.baseUrl}/auth/admin/preferences/`, {
        method: 'PATCH',
        body: JSON.stringify(payload || {}),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error updating admin preferences', { error: error.message });
      throw error;
    }
  }

  /**
   * Update user profile information
   * @param {Object} userData - User data to update (firstName, lastName, email, username, profileIcon)
   * @returns {Promise<Object>} Updated user object
   */
  async updateUserProfile(userData) {
    try {
      const apiData = this.toSnakeCase(userData);
      const data = await this.request(`${this.baseUrl}/auth/users/profile/update/`, {
        method: 'PATCH',
        body: JSON.stringify(apiData),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error updating user profile', { error: error.message });
      throw error;
    }
  }

  /**
   * Change user email (requires current password)
   * @param {Object} data - { newEmail, currentPassword }
   * @returns {Promise<Object>} Updated user object
   */
  async changeEmail(data) {
    try {
      const apiData = this.toSnakeCase(data);
      const result = await this.request(`${this.baseUrl}/auth/users/change-email/`, {
        method: 'POST',
        body: JSON.stringify(apiData),
      });
      return this.toCamelCase(result);
    } catch (error) {
      logger.error('Error changing email', { error: error.message });
      throw error;
    }
  }

  /**
   * Change user password
   * @param {Object} passwordData - Password data (currentPassword, newPassword)
   * @returns {Promise<Object>} Success response
   */
  async changePassword(passwordData) {
    try {
      const apiData = this.toSnakeCase(passwordData);
      const data = await this.request(`${this.baseUrl}/auth/users/change-password/`, {
        method: 'POST',
        body: JSON.stringify(apiData),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error changing password', { error: error.message });
      throw error;
    }
  }

  /** Category follows & notifications (server-backed) */
  async getCategoryFollows() {
    const data = await this.request(`${this.baseUrl}/auth/category-follows/`);
    return this.toCamelCase(data);
  }

  async followCategory(ebayCategoryId) {
    const data = await this.request(`${this.baseUrl}/auth/category-follows/`, {
      method: 'POST',
      body: JSON.stringify({ ebay_category_id: String(ebayCategoryId) }),
    });
    return this.toCamelCase(data);
  }

  async unfollowCategoryById(followId) {
    await this.request(`${this.baseUrl}/auth/category-follows/${followId}/`, {
      method: 'DELETE',
    });
  }

  async unfollowCategoryByEbayId(ebayCategoryId) {
    await this.request(`${this.baseUrl}/auth/category-follows/by-category/${encodeURIComponent(ebayCategoryId)}/`, {
      method: 'DELETE',
    });
  }

  async migrateLocalCategoryFollows(ebayCategoryIds) {
    const results = [];
    for (const id of ebayCategoryIds) {
      try {
        const row = await this.followCategory(id);
        results.push(row);
      } catch (e) {
        logger.warn('Category follow migration skipped for', id, e.message);
      }
    }
    return results;
  }

  async getNotifications({ unreadOnly = false, limit = 20, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (unreadOnly) params.set('unread_only', '1');
    const data = await this.request(`${this.baseUrl}/auth/notifications/?${params}`);
    return this.toCamelCase(data);
  }

  async getNotificationUnreadCount() {
    const data = await this.request(`${this.baseUrl}/auth/notifications/unread-count/`);
    return this.toCamelCase(data);
  }

  async getNotificationCategorySummary() {
    const data = await this.request(`${this.baseUrl}/auth/notifications/category-summary/`);
    return this.toCamelCase(data);
  }

  /**
   * Mark notifications read.
   * Pass keepalive:true when the click that triggers this also navigates away --
   * without it the browser cancels the in-flight request on unload and the
   * notification silently stays unread.
   */
  async markNotificationsRead({ ids = null, all = false, keepalive = false } = {}) {
    const body = all ? { all: true } : { ids };
    const data = await this.request(`${this.baseUrl}/auth/notifications/mark-read/`, {
      method: 'POST',
      body: JSON.stringify(body),
      keepalive,
    });
    return this.toCamelCase(data);
  }

  /** Product watches (server-backed price / availability alerts) */
  async getWatches() {
    const data = await this.request(`${this.baseUrl}/auth/watches/`);
    return this.toCamelCase(data);
  }

  async watchProduct(masterParentId, options = {}) {
    const body = { master_parent_id: Number(masterParentId) };
    // POST is an upsert, so this is also how a threshold gets changed or
    // removed. The three cases have to stay distinct: no `maxPrice` key leaves
    // a saved threshold alone, an empty one clears it, a number sets it.
    // Collapsing the middle case into the first made a threshold permanent
    // once set.
    if ('maxPrice' in options) {
      const raw = options.maxPrice;
      body.max_price = (raw === null || raw === undefined || String(raw).trim() === '')
        ? null
        : Number(raw);
    }
    const data = await this.request(`${this.baseUrl}/auth/watches/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.toCamelCase(data);
  }

  async unwatchById(watchId) {
    await this.request(`${this.baseUrl}/auth/watches/${watchId}/`, {
      method: 'DELETE',
    });
  }

  async unwatchByParentId(masterParentId) {
    await this.request(`${this.baseUrl}/auth/watches/by-parent/${Number(masterParentId)}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Vote on a product
   * @param {string} productId - Product ID
   * @param {string} voteType - 'up' or 'down'
   * @returns {Promise<Object>} Vote object with optional newly_awarded_badges
   */
  async voteProduct(productId, voteType) {
    try {
      const data = await this.request(`${this.baseUrl}/products/${productId}/vote/`, {
        method: 'POST',
        body: JSON.stringify({ vote_type: voteType }),
      });
      return this.toCamelCase(data);
    } catch (error) {
      const errorMessage = error.message || String(error);
      logger.error('Error voting on product', { productId, voteType, error: errorMessage });
      throw error;
    }
  }

  /**
   * Remove vote from a product
   * @param {string} productId - Product ID
   * @returns {Promise<Object>} Success response
   */
  async removeVote(productId) {
    try {
      const data = await this.request(`${this.baseUrl}/products/${productId}/vote/`, {
        method: 'DELETE',
      });
      return this.toCamelCase(data);
    } catch (error) {
      // Check status code first (most reliable)
      if (error.status === 404) {
        logger.debug('Vote not found in database (may be in localStorage)', { productId });
        return { success: true, message: 'Vote not found (already removed)' };
      }
      
      // Also check error message as fallback
      const errorMessage = error.message || String(error);
      const errorString = String(error).toLowerCase();
      if (errorMessage.includes('404') || 
          errorMessage.includes('Not found') || 
          errorMessage.includes('Vote not found') ||
          errorString.includes('404') ||
          errorString.includes('not found') ||
          errorString.includes('vote not found')) {
        logger.debug('Vote not found in database (may be in localStorage)', { productId });
        return { success: true, message: 'Vote not found (already removed)' };
      }
      
      logger.error('Error removing vote', { productId, error: errorMessage });
      throw error;
    }
  }

  /**
   * Get related listings for a product (products with same normalized_product_id)
   * @param {string} productId - Product ID
   * @returns {Promise<Object>} Related listings response with count, results, and metadata
   */
  async getRelatedListings(productId) {
    try {
      const data = await this.request(`${this.baseUrl}/products/${productId}/related-listings/`);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching related listings', { productId, error: error.message });
      // Return empty results on error
      return {
        count: 0,
        results: [],
        hasNormalizedId: false,
        message: 'Failed to load related listings'
      };
    }
  }

  /**
   * Merge two products under one normalized_product_id
   * @param {string} sourceProductId - Source product ID to merge
   * @param {string} targetProductId - Target product ID to merge into
   * @returns {Promise<Object>} Merge response with updated products
   */
  async mergeProducts(sourceProductId, targetProductId) {
    try {
      const data = await this.request(
        `${this.baseUrl}/products/${sourceProductId}/merge/`,
        {
          method: 'POST',
          body: JSON.stringify({ target_product_id: targetProductId, confirm: true })
        }
      );
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error merging products', { sourceProductId, targetProductId, error: error.message });
      throw error;
    }
  }

  /**
   * Get normalization suggestions for products that should be normalized together
   * @param {Object} params - Query parameters (threshold, category, limit, min_group_size)
   * @returns {Promise<Object>} Suggestions response
   */
  async getNormalizationSuggestions(params = {}) {
    try {
      const queryString = this.buildQueryString(params);
      const url = `${this.baseUrl}/products/normalization-suggestions/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching normalization suggestions', { error: error.message });
      throw error;
    }
  }

  /**
   * Get eBay category suggestions based on a keyword
   * @param {string} keyword - Search keyword (e.g., "iPhone 13")
   * @param {boolean} [nocache=false] - If true, bypass server cache (use after deploy or to clear stale empty)
   * @returns {Promise<Object>} Category suggestions response with suggestions array and sandbox_warning flag
   */
  async getCategorySuggestions(keyword, nocache = false) {
    try {
      if (!keyword || !keyword.trim()) {
        throw new Error('Keyword is required');
      }
      const params = { q: keyword.trim() };
      if (nocache) params.nocache = '1';
      const queryString = this.buildQueryString(params);
      const url = `${this.baseUrl}/products/category-suggestions${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching category suggestions', { error: error.message });
      throw error;
    }
  }

  /**
   * Check if a parent with the given name already exists
   * @param {string} name - Parent name to check
   * @returns {Promise<Object|null>} Existing parent if found, null otherwise
   */
  async checkDuplicateParentName(name) {
    try {
      const queryString = this.buildQueryString({ search: name.trim(), include_unavailable: true });
      const url = `${this.baseUrl}/products/master-parents-list/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      const results = data?.results || (Array.isArray(data) ? data : []);
      // Check for exact match (case-insensitive)
      const exactMatch = results.find(p => p.name && p.name.toLowerCase().trim() === name.toLowerCase().trim());
      return exactMatch ? this.toCamelCase(exactMatch) : null;
    } catch (error) {
      logger.error('Error checking duplicate parent name:', error);
      // Don't throw - allow creation to proceed if check fails
      return null;
    }
  }

  /**
   * Create a new master parent
   * @param {Object} parentData - Parent data (name, mainCategory, subcategory)
   * @returns {Promise<Object>} Created master parent
   */
  async createMasterParent(parentData) {
    try {
      const snakeCaseData = this.toSnakeCase(parentData);
      logger.debug('Sending data:', JSON.stringify(snakeCaseData, null, 2));
      const url = `${this.baseUrl}/products/master-parents/`;
      const data = await this.request(url, {
        method: 'POST',
        body: JSON.stringify(snakeCaseData)
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error creating master parent', { error: error.message });
      // Parse Django validation errors if available
      if (error.response) {
        logger.error('Response status:', error.response.status);
        logger.error('Response data:', error.response.data);
        
        const errorData = error.response.data;
        if (errorData) {
          if (typeof errorData === 'object') {
            const errorMessages = [];
            for (const [field, messages] of Object.entries(errorData)) {
              if (Array.isArray(messages)) {
                errorMessages.push(`${field}: ${messages.join(', ')}`);
              } else if (typeof messages === 'object') {
                // Handle nested errors
                for (const [nestedField, nestedMsgs] of Object.entries(messages)) {
                  if (Array.isArray(nestedMsgs)) {
                    errorMessages.push(`${field}.${nestedField}: ${nestedMsgs.join(', ')}`);
                  } else {
                    errorMessages.push(`${field}.${nestedField}: ${nestedMsgs}`);
                  }
                }
              } else {
                errorMessages.push(`${field}: ${messages}`);
              }
            }
            if (errorMessages.length > 0) {
              error.message = errorMessages.join('; ');
            } else if (typeof errorData === 'string') {
              error.message = errorData;
            }
          } else if (typeof errorData === 'string') {
            error.message = errorData;
          }
        }
      }
      throw error;
    }
  }

  /**
   * Delete a master parent
   * @param {string|number} parentId - Master parent ID
   * @returns {Promise<void>}
   */
  async deleteMasterParent(parentId) {
    try {
      const url = `${this.baseUrl}/products/master-parents/${parentId}/`;
      await this.request(url, {
        method: 'DELETE'
      });
    } catch (error) {
      logger.error('Error deleting master parent', { error: error.message });
      throw error;
    }
  }

  /**
   * Get a single master parent by ID
   * @param {string|number} parentId - Master parent ID
   * @returns {Promise<Object>} Master parent data
   */
  async getMasterParent(parentId) {
    try {
      const url = `${this.baseUrl}/products/master-parents/${parentId}/`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error getting master parent:', error);
      throw error;
    }
  }

  /**
   * Get detailed MasterParent information for product detail page
   * @param {string|number} parentId - Master parent ID
   * @returns {Promise<Object>} MasterParent detail with variants and listings
   */
  async getMasterParentDetail(parentId) {
    try {
      const url = `${this.baseUrl}/products/master-parents/${parentId}/full-detail/`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error getting master parent detail:', error);
      throw error;
    }
  }

  /**
   * Update an existing master parent
   * @param {string|number} parentId - Master parent ID
   * @param {Object} parentData - Parent data to update
   * @returns {Promise<Object>} Updated master parent
   */
  async updateMasterParent(parentId, parentData) {
    try {
      const snakeCaseData = this.toSnakeCase(parentData);
      const url = `${this.baseUrl}/products/master-parents/${parentId}/`;
      const data = await this.request(url, {
        method: 'PATCH',
        body: JSON.stringify(snakeCaseData)
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error updating master parent', { error: error.message });
      throw error;
    }
  }

  /**
   * Create a new master variant for a parent
   * @param {string|number} parentId - Master parent ID
   * @param {Object} variantData - Variant data (name, attributes)
   * @returns {Promise<Object>} Created master variant
   */
  async createMasterVariant(parentId, variantData) {
    try {
      const snakeCaseData = this.toSnakeCase(variantData);
      // Use the parent_id in the variant data instead of URL path
      snakeCaseData.parent = parentId;
      const url = `${this.baseUrl}/products/master-variants/`;
      const data = await this.request(url, {
        method: 'POST',
        body: JSON.stringify(snakeCaseData)
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error creating master variant', { error: error.message });
      throw error;
    }
  }

  /**
   * Get a single master variant by ID
   * @param {string|number} variantId - Master variant ID
   * @returns {Promise<Object>} Master variant data
   */
  async getMasterVariant(variantId) {
    try {
      const url = `${this.baseUrl}/products/master-variants/${variantId}/`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error getting master variant:', error);
      throw error;
    }
  }

  /**
   * Update an existing master variant
   * @param {string|number} variantId - Master variant ID
   * @param {Object} variantData - Variant data to update (name, attributes, etc.)
   * @returns {Promise<Object>} Updated master variant
   */
  async updateMasterVariant(variantId, variantData) {
    try {
      const snakeCaseData = this.toSnakeCase(variantData);
      const url = `${this.baseUrl}/products/master-variants/${variantId}/`;
      const data = await this.request(url, {
        method: 'PATCH',
        body: JSON.stringify(snakeCaseData)
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error updating master variant', { error: error.message });
      throw error;
    }
  }

  /**
   * Get variants for a specific master parent
   * @param {string|number} parentId - Master parent ID
   * @returns {Promise<Object>} Object with variants array
   */
  async getVariantsForParent(parentId) {
    try {
      const queryString = this.buildQueryString({ parent: parentId });
      const url = `${this.baseUrl}/products/master-variants/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      
      // Handle array response or paginated response
      let variants = [];
      if (Array.isArray(data)) {
        variants = this.toCamelCase(data);
      } else if (data.results && Array.isArray(data.results)) {
        variants = this.toCamelCase(data.results);
      }
      
      return { variants };
    } catch (error) {
      logger.error('Error fetching variants', { parentId, error: error.message });
      // Return empty variants on error instead of throwing
      return { variants: [] };
    }
  }

  /**
   * Get available categories from approved catalog (eBay + legacy).
   * Used by search filters, categories page, and homepage chips.
   * @returns {Promise<Array<{id: string, label: string, type: string}>>}
   */
  async getAvailableCategories() {
    try {
      const url = `${this.baseUrl}/products/categories/`;
      const data = await this.request(url);
      const list = (data && data.categories && Array.isArray(data.categories)) ? data.categories : [];
      return list.map(item => this.toCamelCase(item));
    } catch (error) {
      logger.error('Error fetching available categories', { error: error.message });
      return [];
    }
  }

  /**
   * Get catalog categories (same dynamic list as getAvailableCategories; requires auth).
   * Used by admin console. Returns categories from approved catalog (eBay + legacy).
   * @returns {Promise<Object>} { categories: [ { id, label, type }, ... ] }
   */
  async getCatalogCategories() {
    try {
      const url = `${this.baseUrl}/products/catalog-categories/`;
      logger.debug('Fetching catalog categories from:', url);
      const data = await this.request(url);
      logger.debug('Catalog categories response:', data);
      if (data && data.categories && Array.isArray(data.categories)) {
        return { categories: data.categories.map(item => this.toCamelCase(item)) };
      }
      return { categories: [] };
    } catch (error) {
      logger.error('Error fetching catalog categories', { 
        error: error.message,
        status: error.status,
        url: `${this.baseUrl}/products/catalog-categories/`,
        response: error.response
      });
      throw error;
    }
  }

  // ==================== HOMEPAGE BUCKETS ====================

  /**
   * Get the three active homepage buckets (public, no auth).
   * @returns {Promise<Array>} [{ id, name, categoryIds }, ...]
   */
  async getHomepageBucketsActive() {
    const data = await this.request(`${this.baseUrl}/products/homepage-buckets/active/`);
    return Array.isArray(data) ? data.map((b) => this.toCamelCase(b)) : [];
  }

  /**
   * List all homepage buckets (admin).
   * @returns {Promise<Array>}
   */
  async getHomepageBuckets() {
    const data = await this.request(`${this.baseUrl}/products/homepage-buckets/`);
    const results = data?.results ?? (Array.isArray(data) ? data : []);
    return results.map((b) => this.toCamelCase(b));
  }

  /**
   * Create a homepage bucket (admin).
   * @param {Object} payload - { name, categoryIds (array), position (optional) }
   * @returns {Promise<Object>}
   */
  async createHomepageBucket(payload) {
    const body = this.toSnakeCase({
      name: payload.name,
      category_ids: payload.categoryIds ?? payload.category_ids ?? [],
      position: payload.position,
    });
    const data = await this.request(`${this.baseUrl}/products/homepage-buckets/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.toCamelCase(data);
  }

  /**
   * Update a homepage bucket (admin).
   * @param {number} id - Bucket id
   * @param {Object} payload - { name, categoryIds }
   * @returns {Promise<Object>}
   */
  async updateHomepageBucket(id, payload) {
    const body = this.toSnakeCase({
      name: payload.name,
      category_ids: payload.categoryIds ?? payload.category_ids,
      position: payload.position,
    });
    const data = await this.request(`${this.baseUrl}/products/homepage-buckets/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return this.toCamelCase(data);
  }

  /**
   * Delete a homepage bucket (admin).
   * @param {number} id - Bucket id
   */
  async deleteHomepageBucket(id) {
    await this.request(`${this.baseUrl}/products/homepage-buckets/${id}/`, { method: 'DELETE' });
  }

  /**
   * Set which three buckets are active (admin). Body: { bucketIds: [id1, id2, id3] }
   * @param {number[]} bucketIds - Exactly 3 bucket ids in display order
   * @returns {Promise<Array>} Updated active buckets
   */
  async reorderHomepageBuckets(bucketIds) {
    const body = this.toSnakeCase({ bucket_ids: bucketIds });
    const data = await this.request(`${this.baseUrl}/products/homepage-buckets/reorder/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return Array.isArray(data) ? data.map((b) => this.toCamelCase(b)) : data;
  }

  // ==================== HOMEPAGE SETTINGS (Featured Deal, Top Categories) ====================

  /**
   * Get the best featured deal candidate (prefers high-resolution images).
   * Public, no auth. Returns single product object or null.
   * @param {Object} [opts] - Optional { excludeProductId: string }
   * @returns {Promise<Object|null>}
   */
  async getFeaturedDealCandidate(opts = {}) {
    const params = {};
    if (opts.excludeProductId) params.exclude_product_id = opts.excludeProductId;
    const query = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    const data = await this.request(`${this.baseUrl}/products/featured-deal-candidate/${query}`);
    return (data && typeof data === 'object' && Object.keys(data).length > 0) ? this.toCamelCase(data) : null;
  }

  /**
   * Get active homepage layout settings (public, no auth).
   * @returns {Promise<{featuredDealId: string|null, topCategoryIds: string[]}>}
   */
  async getHomepageSettingsActive() {
    const data = await this.request(`${this.baseUrl}/products/homepage-settings/active/`);
    return {
      featuredDealId: data?.featured_deal_id ?? data?.featuredDealId ?? null,
      topCategoryIds: data?.top_category_ids ?? data?.topCategoryIds ?? [],
    };
  }

  /**
   * Save the current search as an alert. Authenticated only.
   *
   * SavedSearch matches on query, eBay category, condition family and a price
   * ceiling — and on nothing else. In particular it does not carry the trust
   * floor, so callers must not describe what this creates as an alert that
   * respects the floor.
   *
   * @param {{query: string, ebayCategoryId?: string, conditionFamily?: string, maxPrice?: number|null}} search
   * @returns {Promise<Object>} the created saved search
   */
  async createSavedSearch(search) {
    return this.request(`${this.baseUrl}/auth/saved-searches/`, {
      method: 'POST',
      body: JSON.stringify({
        query: search.query,
        ebayCategoryId: search.ebayCategoryId || '',
        conditionFamily: search.conditionFamily || '',
        maxPrice: search.maxPrice ?? null,
      }),
    });
  }

  /**
   * Homepage "Trending Now" picks, chosen and cached server-side.
   * @param {number} limit - How many items (server clamps to 1..20)
   * @returns {Promise<Array>} camelCased items: productId, name, price, imageUrl, url, mainCategory
   */
  async getTrendingProducts(limit = 8) {
    const data = await this.request(`${this.baseUrl}/products/trending/?limit=${encodeURIComponent(limit)}`);
    const items = Array.isArray(data) ? data : (data && Array.isArray(data.results) ? data.results : []);
    return this.toCamelCase(items);
  }

  /**
   * Get current featured deal (admin).
   * @returns {Promise<{featuredDealId: string|null}>}
   */
  async getHomepageFeaturedDeal() {
    const data = await this.request(`${this.baseUrl}/products/homepage-settings/featured-deal/`);
    return {
      featuredDealId: data?.featured_deal_id ?? data?.featuredDealId ?? null,
      previewStatus: data?.preview_status ?? data?.previewStatus ?? null,
    };
  }

  /**
   * Paginated searchable picker for featured deal (admin).
   * @param {{search?: string, page?: number, pageSize?: number}} params
   * @returns {Promise<{count: number, page: number, pageSize: number, results: Array}>}
   */
  async getFeaturedDealPicker(params = {}) {
    const queryParams = {};
    if (params.search) queryParams.search = params.search;
    if (params.page != null) queryParams.page = params.page;
    if (params.pageSize != null) queryParams.page_size = params.pageSize;
    const queryString = this.buildQueryString(queryParams);
    const data = await this.request(
      `${this.baseUrl}/products/homepage-settings/featured-deal-picker/${queryString ? '?' + queryString : ''}`
    );
    return {
      count: data?.count ?? 0,
      page: data?.page ?? 1,
      pageSize: data?.page_size ?? data?.pageSize ?? 12,
      results: Array.isArray(data?.results) ? data.results.map((r) => this.toCamelCase(r)) : [],
    };
  }

  /**
   * Check eBay product freshness (JIT refresh).
   * @param {string} productId
   * @returns {Promise<{status: string, isFresh?: boolean, message?: string}>}
   */
  async checkProductFreshness(productId) {
    const encodedId = encodeURIComponent(productId);
    return this.request(`${this.baseUrl}/products/${encodedId}/check-freshness/`);
  }

  /**
   * Set featured deal (admin).
   * @param {string|null} productId - product_id or null to clear
   * @returns {Promise<{featuredDealId: string|null}>}
   */
  async setHomepageFeaturedDeal(productId) {
    const body = productId ? { product_id: productId } : {};
    const data = await this.request(`${this.baseUrl}/products/homepage-settings/featured-deal/`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return { featuredDealId: data?.featured_deal_id ?? data?.featuredDealId ?? null };
  }

  /**
   * Get top categories (admin).
   * @returns {Promise<{topCategoryIds: string[]}>}
   */
  async getHomepageTopCategories() {
    const data = await this.request(`${this.baseUrl}/products/homepage-settings/top-categories/`);
    return {
      topCategoryIds: data?.top_category_ids ?? data?.topCategoryIds ?? [],
    };
  }

  /**
   * Set top categories (admin).
   * @param {string[]} categoryIds - Main category names in order
   * @returns {Promise<{topCategoryIds: string[]}>}
   */
  async setHomepageTopCategories(categoryIds) {
    const data = await this.request(`${this.baseUrl}/products/homepage-settings/top-categories/`, {
      method: 'PUT',
      body: JSON.stringify({ category_ids: categoryIds }),
    });
    return {
      topCategoryIds: data?.top_category_ids ?? data?.topCategoryIds ?? [],
    };
  }

  /**
   * Get available categories for admin dropdown (inventory-based).
   * Same shape as getAvailableCategories: { categories: [ { id, label, shortLabel, fullLabel, type }, ... ] }
   * @returns {Promise<{categories: Array<{id: string, label: string, shortLabel?: string, fullLabel?: string, type: string}>}>}
   */
  async getHomepageMainCategories() {
    const data = await this.request(`${this.baseUrl}/products/homepage-settings/main-categories/`);
    const raw = data?.categories ?? data?.main_categories ?? data?.mainCategories ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return {
      categories: list.map((c) => (typeof c === 'string' ? { id: c, label: c } : this.toCamelCase(c))),
    };
  }

  /**
   * List homepage bucket presets (admin).
   * @returns {Promise<Array>}
   */
  async getHomepagePresets() {
    const data = await this.request(`${this.baseUrl}/products/homepage-buckets/presets/`);
    const results = data?.results ?? (Array.isArray(data) ? data : []);
    return results.map((p) => this.toCamelCase(p));
  }

  /**
   * Create a homepage bucket preset (admin). Saves current three bucket ids.
   * @param {string} name - Preset name
   * @param {number[]} bucketIds - Exactly 3 bucket ids in order
   * @returns {Promise<Object>}
   */
  async saveHomepagePreset(name, bucketIds) {
    const body = this.toSnakeCase({ name, bucket_ids: bucketIds });
    const data = await this.request(`${this.baseUrl}/products/homepage-buckets/presets/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.toCamelCase(data);
  }

  /**
   * Apply a preset: set its three buckets to positions 1–3 (admin).
   * @param {number} presetId - Preset id
   * @returns {Promise<Array>} New active buckets
   */
  async loadHomepagePreset(presetId) {
    const data = await this.request(
      `${this.baseUrl}/products/homepage-buckets/presets/${presetId}/load/`,
      { method: 'POST' }
    );
    return Array.isArray(data) ? data.map((b) => this.toCamelCase(b)) : data;
  }

  /**
   * Get master parents list with optional filtering and pagination
   * @param {Object} params - Query parameters (page, ordering, search, duplicates_only)
   * @returns {Promise<Object>} Paginated response with results and count
   */
  async getMasterParentsList(params = {}) {
    try {
      const queryString = this.buildQueryString(params);
      const url = `${this.baseUrl}/products/master-parents-list/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      
      // Handle paginated response (DRF returns {count, next, previous, results: [...]})
      // or plain array response
      if (data) {
        if (Array.isArray(data)) {
          // If it's a plain array, wrap it in pagination format
          return {
            count: data.length,
            results: this.toCamelCase(data),
            pageSize: params.page_size || 20
          };
        } else if (data.results && Array.isArray(data.results)) {
          // Paginated response - convert results to camelCase but keep pagination metadata
          return {
            ...data,
            results: this.toCamelCase(data.results),
            pageSize: data.page_size || params.page_size || 20
          };
        }
      }
      
      // Return empty response if no data
      return {
        count: 0,
        results: [],
        pageSize: params.page_size || 20
      };
    } catch (error) {
      logger.error('Error fetching master parents list', { error: error.message });
      throw error;
    }
  }

  /**
   * Batch normalize multiple product groups
   * @param {Array} groups - Array of groups, each with product_ids and optional normalized_id
   * @returns {Promise<Object>} Batch normalize response
   */
  async batchNormalizeProducts(groups) {
    try {
      const data = await this.request(
        `${this.baseUrl}/products/batch-normalize/`,
        {
          method: 'POST',
          body: JSON.stringify({ groups })
        }
      );
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error batch normalizing products:', error);
      throw error;
    }
  }

  /**
   * Batch unnormalize products by removing their normalized_product_id
   * @param {Array} productIds - Array of product IDs to unnormalize
   * @param {boolean} unnormalizeGroup - If true, unnormalize all products with same normalized_id
   * @returns {Promise<Object>} Batch unnormalize response
   */
  async batchUnnormalizeProducts(productIds, unnormalizeGroup = false) {
    try {
      const data = await this.request(
        `${this.baseUrl}/products/batch-unnormalize/`,
        {
          method: 'POST',
          body: JSON.stringify({
            product_ids: productIds,
            unnormalize_group: unnormalizeGroup
          })
        }
      );
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error unnormalizing products:', error);
      throw error;
    }
  }

  /**
   * Merge two MasterParent instances
   * Moves all variants from source to target and deletes source
   * @param {number} sourceId - Source MasterParent ID
   * @param {number} targetId - Target MasterParent ID
   * @returns {Promise<Object>} Merge result with variantsMoved, productsUpdated, etc.
   */
  async mergeMasterParents(sourceId, targetId) {
    try {
      // Try the action endpoint first
      const url = `${this.baseUrl}/products/master-parents/${sourceId}/merge/`;
      const data = await this.request(url, {
        method: 'POST',
        body: JSON.stringify({ target_id: targetId })
      });
      return this.toCamelCase(data);
    } catch (error) {
      // If 404, the endpoint doesn't exist - we'll need to implement it
      if (error.status === 404 || error.message?.includes('404')) {
        logger.warn('Merge endpoint not found, attempting manual merge via API calls');
        // Fallback: manually move variants and delete source
        return await this.mergeMasterParentsManual(sourceId, targetId);
      }
      logger.error('Error merging master parents:', error);
      throw error;
    }
  }

  /**
   * Manual merge fallback - moves variants and deletes source
   * @param {number} sourceId - Source MasterParent ID
   * @param {number} targetId - Target MasterParent ID
   * @returns {Promise<Object>} Merge result
   */
  async mergeMasterParentsManual(sourceId, targetId) {
    try {
      // Get source variants - returns { variants: [...] }
      const response = await this.getVariantsForParent(sourceId);
      const sourceVariants = response.variants || response || [];
      
      // Ensure it's an array
      if (!Array.isArray(sourceVariants)) {
        throw new Error(`Expected array of variants, got ${typeof sourceVariants}`);
      }
      
      let variantsMoved = 0;
      let productsUpdated = 0;

      // Move each variant to target
      for (const variant of sourceVariants) {
        try {
          await this.updateMasterVariant(variant.id, {
            parent_id: targetId
          });
          variantsMoved++;
        } catch (error) {
          logger.error('Error moving variant', { variantId: variant.id, error: error.message });
        }
      }

      // Delete source parent
      await this.deleteMasterParent(sourceId);

      return {
        variantsMoved,
        productsUpdated,
        success: true,
        message: `Merged ${variantsMoved} variant(s) from source to target`
      };
    } catch (error) {
      logger.error('Error in manual merge:', error);
      throw error;
    }
  }

  /**
   * Get unnormalized products grouped by confidence-matched Master Parents
   * @returns {Promise<Object>} Grouped suggestions with high_confidence, medium_confidence, and no_match arrays
   */
  async getUnnormalizedGroups() {
    try {
      const url = `${this.baseUrl}/products/unnormalized-groups/`;
      const response = await this.request(url);
      return this.toCamelCase(response);
    } catch (error) {
      logger.error('Error fetching unnormalized groups', { error: error.message });
      throw error;
    }
  }

  /**
   * Get orphaned variants (variants with no linked product) with suggested matches
   * @returns {Promise<Object>} Orphaned variants with suggestions
   */
  async getOrphanedVariants() {
    try {
      const url = `${this.baseUrl}/products/master-variants/orphaned-variants/`;
      const response = await this.request(url);
      return this.toCamelCase(response);
    } catch (error) {
      logger.error('Error fetching orphaned variants', { error: error.message });
      throw error;
    }
  }

  /**
   * Link a MasterVariant to a Product
   * @param {number} variantId - MasterVariant ID
   * @param {string} productId - Product.product_id (not Product.id)
   * @returns {Promise<Object>} Link result
   */
  async linkVariantToProduct(variantId, productId) {
    try {
      const url = `${this.baseUrl}/products/master-variants/link-variant/`;
      const response = await this.request(url, {
        method: 'POST',
        body: JSON.stringify({
          variant_id: variantId,
          product_id: productId
        })
      });
      return this.toCamelCase(response);
    } catch (error) {
      logger.error('Error linking variant to product:', error);
      throw error;
    }
  }

  /**
   * Batch map multiple product listings to a master parent
   * @param {number} masterParentId - Master Parent ID
   * @param {Array<string>} productIds - Array of product IDs to map
   * @param {boolean} createVariant - Whether to create a new variant
   * @param {string} variantName - Name for the variant (if creating)
   * @returns {Promise<Object>} Mapping result
   */
  async batchMapListings(masterParentId, productIds, createVariant = false, variantName = 'Default') {
    try {
      const url = `${this.baseUrl}/products/batch-map-listings/`;
      const response = await this.request(url, {
        method: 'POST',
        body: JSON.stringify({
          master_parent_id: masterParentId,
          product_ids: productIds,
          create_variant: createVariant,
          variant_name: variantName
        })
      });
      return this.toCamelCase(response);
    } catch (error) {
      logger.error('Error batch mapping listings:', error);
      throw error;
    }
  }

  /**
   * Get products ranked by match probability
   * @param {number} limit - Maximum number of products to return (default: 100)
   * @param {Object} params - Optional parameters including view_mode and search
   * @returns {Promise<Object>} Products ranked by match probability
   */
  async getProductsRankedByMatchProbability(limit = 100, params = {}) {
    try {
      const queryParams = { limit, ...params };
      const queryString = this.buildQueryString(queryParams);
      const url = `${this.baseUrl}/products/ranked-by-match-probability/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error getting ranked products:', error);
      throw error;
    }
  }

  // ==================== LEADERBOARDS ====================

  /**
   * Get leaderboard data
   * @param {string} leaderboardType - e.g., 'reputation_all_time'
   * @returns {Promise<Array>} Leaderboard data
   */
  async getLeaderboard(leaderboardType) {
    try {
      const url = `${this.baseUrl}/leaderboards/${leaderboardType}/`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching leaderboard', { leaderboardType, error: error.message });
      throw error;
    }
  }

  // ==================== MY VOTES (Activity Center) ====================

  /**
   * Get current user's votes with product context for Activity Center
   * @param {number} [limit=100] - Max number of votes to return
   * @returns {Promise<Array>} Array of { id, voteType, createdAt, product: { id, productId, name, affiliateLink } }
   */
  async getMyVotes(limit = 100) {
    try {
      const url = `${this.baseUrl}/products/my-votes/?limit=${limit}`;
      const data = await this.request(url);
      return Array.isArray(data) ? this.toCamelCase(data) : [];
    } catch (error) {
      logger.error('Error fetching my votes', { error: error.message });
      throw error;
    }
  }

  // ==================== SAVED ITEMS ====================

  /**
   * Get user's saved items
   * @returns {Promise<Array>} Array of saved items
   */
  async getSavedItems() {
    try {
      const url = `${this.baseUrl}/saved-items/`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching saved items', { error: error.message });
      throw error;
    }
  }

  /**
   * Save an item
   * @param {Object} itemData - Item data to save
   * @returns {Promise<Object>} Saved item
   */
  async saveItem(itemData) {
    try {
      const url = `${this.baseUrl}/saved-items/`;
      const snakeCaseData = this.toSnakeCase(itemData);
      const data = await this.request(url, {
        method: 'POST',
        body: JSON.stringify(snakeCaseData),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error saving item:', error);
      throw error;
    }
  }

  /**
   * Unsave an item
   * @param {number|string} itemId - ID of saved item to remove
   * @returns {Promise<void>}
   */
  async unsaveItem(itemId) {
    try {
      const url = `${this.baseUrl}/saved-items/${itemId}/`;
      await this.request(url, {
        method: 'DELETE',
      });
    } catch (error) {
      logger.error('Error unsaving item:', error);
      throw error;
    }
  }

  // ==================== USER FINDS ====================

  /**
   * Get user's finds
   * @returns {Promise<Array>} Array of user finds
   */
  async getUserFinds() {
    try {
      const url = `${this.baseUrl}/finds/`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching user finds', { error: error.message });
      throw error;
    }
  }

  /**
   * Add a find
   * @param {string} productId - Product ID to add as a find
   * @returns {Promise<Object>} Created find
   */
  async addUserFind(productId) {
    try {
      const url = `${this.baseUrl}/finds/`;
      const data = await this.request(url, {
        method: 'POST',
        body: JSON.stringify({ product_id: productId }),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error adding user find:', error);
      throw error;
    }
  }

  /**
   * Remove a find
   * @param {number|string} findId - ID of find to remove
   * @returns {Promise<void>}
   */
  async removeUserFind(findId) {
    try {
      const url = `${this.baseUrl}/finds/${findId}/`;
      await this.request(url, {
        method: 'DELETE',
      });
    } catch (error) {
      logger.error('Error removing user find:', error);
      throw error;
    }
  }

  // ==================== LOCALSTORAGE FALLBACKS ====================

  /**
   * Fallback: Get products from localStorage
   */
  getProductsFromLocalStorage() {
    try {
      const storedItems = localStorage.getItem('usedfindr_items');
      return storedItems ? JSON.parse(storedItems) : [];
    } catch (error) {
      logger.error('Error reading products from localStorage:', error);
      return [];
    }
  }

  /**
   * Fallback: Save product to localStorage
   */
  saveProductToLocalStorage(productData) {
    try {
      const storedItems = localStorage.getItem('usedfindr_items');
      const items = storedItems ? JSON.parse(storedItems) : [];
      items.push({
        ...productData,
        id: Date.now().toString(),
      });
      localStorage.setItem('usedfindr_items', JSON.stringify(items));
      return productData;
    } catch (error) {
      logger.error('Error saving product to localStorage:', error);
      throw error;
    }
  }

  /**
   * Fallback: Get reports from localStorage
   */
  getReportsFromLocalStorage() {
    try {
      const reports = localStorage.getItem('usedfindr_reports');
      return reports ? JSON.parse(reports) : [];
    } catch (error) {
      logger.error('Error reading reports from localStorage:', error);
      return [];
    }
  }

  /**
   * Fallback: Save report to localStorage
   */
  saveReportToLocalStorage(reportData) {
    try {
      const reports = localStorage.getItem('usedfindr_reports');
      const reportsArray = reports ? JSON.parse(reports) : [];
      reportsArray.push({
        ...reportData,
        id: reportData.id || Date.now().toString(),
      });
      localStorage.setItem('usedfindr_reports', JSON.stringify(reportsArray));
      return reportData;
    } catch (error) {
      logger.error('Error saving report to localStorage:', error);
      throw error;
    }
  }

  /**
   * Search for items from trusted sellers
   * 
   * @param {Object} params - Search parameters
   * @param {string} params.q - Search keyword (required)
   * @param {number} [params.minPrice] - Minimum price filter
   * @param {number} [params.maxPrice] - Maximum price filter
   * @param {string} [params.categoryId] - eBay category ID
   * @param {string} [params.listingType] - 'FIXED_PRICE', 'AUCTION', or 'BOTH' (default: 'FIXED_PRICE')
   * @param {string} [params.condition] - 'New', 'Refurb', or 'Used'
   * @param {number} [params.minSellerFeedbackPct] - Minimum seller feedback percentage (default: 99.5)
   * @param {number} [params.minSellerReviews] - Minimum seller reviews (default: 50)
   * @param {string} [params.sortOrder] - 'priceAscending', 'priceDescending', 'sellerScoreDescending', 'newlyListed', 'bestMatch' (default: 'priceAscending')
   * @param {number} [params.page] - Page number (default: 1)
   * @param {number} [params.pageSize] - Items per page (default: 20)
   * @returns {Promise<Object>} Paginated results with seller information
   */
  async searchTrustedSellers(params = {}) {
    try {
      // Validate required parameter
      if (!params.q || !params.q.trim()) {
        throw new Error('Search keyword (q) is required');
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('q', params.q.trim());
      
      if (params.minPrice !== undefined && params.minPrice !== null) {
        queryParams.append('min_price', params.minPrice.toString());
      }
      if (params.maxPrice !== undefined && params.maxPrice !== null) {
        queryParams.append('max_price', params.maxPrice.toString());
      }
      if (params.categoryId) {
        queryParams.append('category_id', params.categoryId);
      }
      if (params.listingType) {
        queryParams.append('listing_type', params.listingType);
      }
      if (params.condition) {
        queryParams.append('condition', params.condition);
      }
      if (params.returnsAccepted === true || params.returns_accepted === true) {
        queryParams.append('returns_accepted', 'true');
      }
      if (params.freeShipping === true || params.free_shipping === true) {
        queryParams.append('free_shipping', 'true');
      }
      if (params.minQuantity !== undefined && params.minQuantity !== null && params.minQuantity >= 1) {
        queryParams.append('min_quantity', params.minQuantity.toString());
      }
      if (params.includeForParts === true || params.include_for_parts === true) {
        queryParams.append('include_for_parts', 'true');
      }
      if (params.skipFeedback === true) {
        queryParams.append('skip_feedback', 'true');
      } else if (params.minSellerFeedbackPct !== undefined && params.minSellerFeedbackPct !== null) {
        queryParams.append('min_seller_feedback_pct', params.minSellerFeedbackPct.toString());
      }
      if (params.skipReviews === true) {
        queryParams.append('skip_reviews', 'true');
      } else if (params.minSellerReviews !== undefined && params.minSellerReviews !== null) {
        queryParams.append('min_seller_reviews', params.minSellerReviews.toString());
      }
      if (params.sortOrder) {
        queryParams.append('sort_order', params.sortOrder);
      }
      if (params.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params.pageSize) {
        queryParams.append('page_size', params.pageSize.toString());
      }

      // Make GET request
      const url = `/public/trusted-search/?${queryParams.toString()}`;
      const response = await this.get(url);

      // Response is already in camelCase from handleResponse
      // But we need to ensure seller data is properly formatted
      if (response && response.results) {
        response.results = response.results.map(item => ({
          ...item,
          // Ensure seller data fields are present
          sellerFeedbackPct: item.sellerFeedbackPct || item.seller_feedback_pct,
          sellerSalesCount: item.sellerSalesCount || item.seller_sales_count,
        }));
      }

      return response;
    } catch (error) {
      logger.error('Error searching trusted sellers:', error);
      throw error;
    }
  }

  /**
   * Send consented engagement analytics events (batched).
   * @param {Array<Object>} events - Engagement event payloads
   * @returns {Promise<Object>}
   */
  async trackEngagementEvents(events = []) {
    try {
      if (!Array.isArray(events) || events.length === 0) {
        return { success: true, accepted: 0, ignored: 0 };
      }
      const url = '/products/engagement-analytics/ingest/';
      return await this.post(url, { events });
    } catch (error) {
      logger.warn('Failed to track engagement events:', error);
      throw error;
    }
  }

  /**
   * Get backend summary for admin overview engagement analytics.
   * @param {Object} filters - Optional filters (start_date, end_date, category, product_id)
   * @returns {Promise<Object>}
   */
  async getEngagementAnalyticsSummary(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.product_id) queryParams.append('product_id', filters.product_id);
      const qs = queryParams.toString();
      const url = qs
        ? `/products/engagement-analytics/summary/?${qs}`
        : '/products/engagement-analytics/summary/';
      return await this.get(url);
    } catch (error) {
      logger.error('Error loading engagement analytics summary:', error);
      throw error;
    }
  }

  /**
   * Get cookieless site-traffic summary for admin secondary analytics tab.
   * @param {Object} filters - start_date, end_date, any COOKIELESS_FILTER_KEYS
   *   entry, and optionally `list` + `limit` to fetch one list at full depth.
   * @returns {Promise<Object>}
   */
  async getCookielessAnalyticsSummary(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      COOKIELESS_FILTER_KEYS.forEach((key) => {
        const value = filters[key];
        if (value !== undefined && value !== null && String(value) !== '') {
          queryParams.append(key, String(value));
        }
      });
      if (filters.list) {
        queryParams.append('list', String(filters.list));
        if (filters.limit) queryParams.append('limit', String(filters.limit));
      }
      const qs = queryParams.toString();
      const url = qs
        ? `/products/cookieless-analytics/summary/?${qs}`
        : '/products/cookieless-analytics/summary/';
      return await this.get(url);
    } catch (error) {
      logger.error('Error loading cookieless analytics summary:', error);
      throw error;
    }
  }

  /**
   * Track trusted search analytics
   * @param {Object} params - Search parameters
   * @param {number} resultsCount - Number of results returned
   */
  async trackTrustedSearch(params = {}, resultsCount = null) {
    try {
      // Only track if search term is present
      if (!params.q || !params.q.trim()) {
        return;
      }

      // Prepare data for analytics
      const analyticsData = {
        search_term: params.q.trim(),
        search_source: 'trusted_finder',
        min_price: params.minPrice || null,
        max_price: params.maxPrice || null,
        listing_type: params.listingType || 'FIXED_PRICE',
        condition: params.condition || null,
        min_seller_feedback_pct: params.minSellerFeedbackPct || null,
        min_seller_reviews: params.minSellerReviews || null,
        category_id: params.categoryId || null,
        sort_order: params.sortOrder || 'priceAscending',
        results_count: resultsCount,
      };

      // Remove null/undefined values
      Object.keys(analyticsData).forEach(key => {
        if (analyticsData[key] === null || analyticsData[key] === undefined) {
          delete analyticsData[key];
        }
      });

      // Make POST request (non-blocking, errors are caught and logged)
      const url = '/products/trusted-search-analytics/';
      await this.post(url, analyticsData);
    } catch (error) {
      // Log error but don't throw - analytics should never disrupt search functionality
      logger.warn('Failed to track search analytics:', error);
    }
  }

  /**
   * Track catalog (search.html) text search for admin analytics.
   * @param {string} query - Search query
   * @param {number|null} resultsCount - Number of items shown after local filters
   */
  async trackCatalogSearch(query, resultsCount = null) {
    try {
      const q = query != null ? String(query).trim() : '';
      if (!q) {
        return;
      }
      const analyticsData = {
        search_term: q,
        search_source: 'catalog',
        results_count: resultsCount,
      };
      Object.keys(analyticsData).forEach((key) => {
        if (analyticsData[key] === null || analyticsData[key] === undefined) {
          delete analyticsData[key];
        }
      });
      const url = '/products/trusted-search-analytics/';
      await this.post(url, analyticsData);
    } catch (error) {
      logger.warn('Failed to track catalog search analytics:', error);
    }
  }

  /**
   * Record a minimal successful search (no analytics consent; no auth header).
   * POST /api/analytics/anonymous-search/ — only search_term + search_source.
   */
  async postAnonymousSearch({ search_term: term, search_source: searchSource }) {
    try {
      const q = term != null ? String(term).trim() : '';
      if (!q || !searchSource) {
        return;
      }
      const body = JSON.stringify({
        search_term: q,
        search_source: searchSource,
      });
      await this.request(`${this.baseUrl}/analytics/anonymous-search/`, {
        method: 'POST',
        body,
        omitAuthorization: true,
      });
    } catch (error) {
      logger.warn('Failed to post anonymous search stat:', error);
    }
  }

  /**
   * Admin aggregate for anonymous search stats (totals, top terms, daily_counts).
   * In admin context, request is rewritten to /api/admin/analytics/anonymous-search/.
   */
  async getAnonymousSearchAnalytics(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.start_date) {
        queryParams.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        queryParams.append('end_date', filters.end_date);
      }
      if (filters.aggregate) {
        queryParams.append('aggregate', 'true');
      }
      if (filters.search_source) {
        queryParams.append('search_source', filters.search_source);
      }
      if (filters.search_term) {
        queryParams.append('search_term', filters.search_term);
      }
      const url = `/analytics/anonymous-search/?${queryParams.toString()}`;
      return await this.get(url);
    } catch (error) {
      logger.error('Error getting anonymous search analytics:', error);
      throw error;
    }
  }

  /**
   * Get trusted search analytics data
   * @param {Object} filters - Optional filters (start_date, end_date, search_term, listing_type, aggregate, page, page_size, search_source)
   * @returns {Promise<Object>} Analytics data
   */
  async getTrustedSearchAnalytics(filters = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.start_date) {
        queryParams.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        queryParams.append('end_date', filters.end_date);
      }
      if (filters.search_term) {
        queryParams.append('search_term', filters.search_term);
      }
      if (filters.listing_type) {
        queryParams.append('listing_type', filters.listing_type);
      }
      if (filters.aggregate) {
        queryParams.append('aggregate', 'true');
      }
      if (filters.search_source) {
        queryParams.append('search_source', filters.search_source);
      }
      if (filters.page) {
        queryParams.append('page', filters.page.toString());
      }
      if (filters.page_size) {
        queryParams.append('page_size', filters.page_size.toString());
      }

      const url = `/products/trusted-search-analytics/?${queryParams.toString()}`;
      const response = await this.get(url);
      return response;
    } catch (error) {
      logger.error('Error getting search analytics:', error);
      throw error;
    }
  }

  /**
   * Get eBay API usage analytics (admin only)
   * @param {Object} filters - Optional filters (start_date, end_date, date for by_hour, page, page_size for call_log)
   * @returns {Promise<Object>} { summary, by_day, by_method, by_source, by_hour, call_log }
   */
  async getEbayApiUsage(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.start_date) {
        queryParams.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        queryParams.append('end_date', filters.end_date);
      }
      if (filters.date) {
        queryParams.append('date', filters.date);
      }
      if (filters.page != null) {
        queryParams.append('page', String(filters.page));
      }
      if (filters.page_size != null) {
        queryParams.append('page_size', String(filters.page_size));
      }
      const url = `/products/ebay-api-usage/?${queryParams.toString()}`;
      const response = await this.get(url);
      return response;
    } catch (error) {
      logger.error('Error getting eBay API usage:', error);
      throw error;
    }
  }

  /**
   * Get eBay compliance analytics (admin only)
   * @param {Object} params - Optional params (page, page_size)
   * @returns {Promise<Object>} { summary: { total_requests, last_ping_date }, results, page, page_size }
   */
  async getEbayComplianceAnalytics(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page != null) {
        queryParams.append('page', params.page);
      }
      if (params.page_size != null) {
        queryParams.append('page_size', params.page_size);
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }
      const url = `/products/ebay-compliance-analytics${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await this.get(url);
      return response;
    } catch (error) {
      logger.error('Error getting eBay compliance analytics:', error);
      throw error;
    }
  }

  // ==================== ADMIN USER MANAGEMENT ====================

  /**
   * Get all users (admin only)
   * @param {Object} params - Query parameters (search, status, ordering)
   * @returns {Promise<Array>} Array of users
   */
  async getAdminUsers(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${this.baseUrl}/auth/admin/users${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching admin users', { error: error.message });
      throw error;
    }
  }

  /**
   * Get user details (admin only)
   * @param {number|string} userId - User ID
   * @returns {Promise<Object>} User object
   */
  async getAdminUser(userId) {
    try {
      const url = `${this.baseUrl}/auth/admin/users/${userId}/`;
      const data = await this.request(url);
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error fetching admin user', { error: error.message });
      throw error;
    }
  }

  /**
   * Lock user account (admin only)
   * @param {number|string} userId - User ID
   * @returns {Promise<Object>} Updated user object
   */
  async lockUser(userId) {
    try {
      const url = `${this.baseUrl}/auth/admin/users/${userId}/lock/`;
      const data = await this.request(url, {
        method: 'PATCH',
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error locking user:', error);
      throw error;
    }
  }

  /**
   * Unlock user account (admin only)
   * @param {number|string} userId - User ID
   * @returns {Promise<Object>} Updated user object
   */
  async unlockUser(userId) {
    try {
      const url = `${this.baseUrl}/auth/admin/users/${userId}/unlock/`;
      const data = await this.request(url, {
        method: 'PATCH',
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error unlocking user:', error);
      throw error;
    }
  }

  /**
   * Update user newsletter opt-in (admin only)
   * @param {number|string} userId - User ID
   * @param {boolean} newsletterOptIn
   * @returns {Promise<Object>} Updated user object
   */
  async updateUserNewsletter(userId, newsletterOptIn) {
    try {
      const url = `${this.baseUrl}/auth/admin/users/${userId}/newsletter/`;
      const data = await this.request(url, {
        method: 'PATCH',
        body: JSON.stringify({ newsletter_opt_in: newsletterOptIn }),
      });
      return this.toCamelCase(data);
    } catch (error) {
      logger.error('Error updating user newsletter:', error);
      throw error;
    }
  }

  /**
   * Get newsletter subscribers (admin only)
   * @param {Object} params - Query params (search, type, status)
   * @returns {Promise<Array>}
   */
  async getNewsletterSubscribers(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${this.baseUrl}/auth/admin/newsletter-subscribers/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      logger.error('Error fetching newsletter subscribers:', error);
      throw error;
    }
  }

  /**
   * Delete anonymous newsletter subscriber (admin only)
   * @param {number} subscriberId - NewsletterSubscriber ID
   * @returns {Promise<void>}
   */
  async deleteNewsletterSubscriber(subscriberId) {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter-subscribers/${subscriberId}/`;
      await this.request(url, { method: 'DELETE' });
    } catch (error) {
      logger.error('Error deleting newsletter subscriber:', error);
      throw error;
    }
  }

  /**
   * Get newsletter recipient counts (admin send permissions required)
   */
  async getNewsletterRecipientCount() {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter-subscribers/count/`;
      const data = await this.request(url);
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error fetching newsletter recipient counts:', error);
      throw error;
    }
  }

  /**
   * Send newsletter immediately
   * @param {Object} payload - {subject, body_html, body_text}
   */
  async sendNewsletter(payload) {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/send/`;
      const data = await this.request(url, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error sending newsletter:', error);
      throw error;
    }
  }

  /**
   * Schedule newsletter
   * @param {Object} payload - {subject, body_html, body_text, scheduled_for}
   */
  async scheduleNewsletter(payload) {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/schedule/`;
      const data = await this.request(url, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error scheduling newsletter:', error);
      throw error;
    }
  }

  /**
   * Cancel a scheduled newsletter by send log ID
   */
  async cancelScheduledNewsletter(logId) {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/schedule/${logId}/`;
      const data = await this.request(url, { method: 'DELETE' });
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error cancelling scheduled newsletter:', error);
      throw error;
    }
  }

  /**
   * Get newsletter send history
   * @param {Object} params - {limit, offset}
   */
  async getNewsletterHistory(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${this.baseUrl}/auth/admin/newsletter/history/${queryString ? '?' + queryString : ''}`;
      const data = await this.request(url);
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error fetching newsletter history:', error);
      throw error;
    }
  }

  /**
   * Get auto-generated deal alert preview
   */
  async getDealAlertPreview() {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/preview-deal-alert/`;
      const data = await this.request(url);
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error fetching deal alert preview:', error);
      throw error;
    }
  }

  /**
   * List newsletter templates
   */
  async getNewsletterTemplates() {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/templates/`;
      const data = await this.request(url);
      return Array.isArray(data) ? this.toCamelCase(data) : [];
    } catch (error) {
      logger.error('Error fetching newsletter templates:', error);
      throw error;
    }
  }

  /**
   * Fetch one newsletter template
   */
  async getNewsletterTemplate(templateId) {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/templates/${templateId}/`;
      const data = await this.request(url);
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error fetching newsletter template:', error);
      throw error;
    }
  }

  /**
   * Create newsletter template
   */
  async createNewsletterTemplate(payload) {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/templates/`;
      const data = await this.request(url, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error creating newsletter template:', error);
      throw error;
    }
  }

  /**
   * Update newsletter template
   */
  async updateNewsletterTemplate(templateId, payload) {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/templates/${templateId}/`;
      const data = await this.request(url, {
        method: 'PATCH',
        body: JSON.stringify(payload || {}),
      });
      return this.toCamelCase(data || {});
    } catch (error) {
      logger.error('Error updating newsletter template:', error);
      throw error;
    }
  }

  /**
   * Delete newsletter template
   */
  async deleteNewsletterTemplate(templateId) {
    try {
      const url = `${this.baseUrl}/auth/admin/newsletter/templates/${templateId}/`;
      await this.request(url, { method: 'DELETE' });
    } catch (error) {
      logger.error('Error deleting newsletter template:', error);
      throw error;
    }
  }

  /**
   * Delete user account (soft delete - admin only)
   * @param {number|string} userId - User ID
   * @returns {Promise<void>}
   */
  async deleteUser(userId) {
    try {
      const url = `${this.baseUrl}/auth/admin/users/${userId}/`;
      logger.debug('deleteUser called:', { userId, url });
      const response = await this.request(url, {
        method: 'DELETE',
      });
      logger.debug('deleteUser success:', { userId, response });
      return response;
    } catch (error) {
      logger.error('Error deleting user:', error);
      logger.error('Delete error details:', {
        userId,
        status: error.status,
        message: error.message,
        body: error.body,
        response: error.response
      });
      throw error;
    }
  }

}

// Create global instance
const apiService = new ApiService();
window.apiService = apiService;

// Export for module usage
export { ApiService, apiService };

